
// frontend/src/pages/TourBuilderPage.jsx
// Step 2 opens with the SIO recommendation gallery (priced category blueprints).
// Applying a blueprint seeds parks / nights / zones / lodges, then reveals the
// normal customization UI (StepParksLodgesExperiences). "Design from scratch"
// skips straight to manual mode.
//
// PRICING: once a blueprint is applied, Steps 1–5 anchor on the SIO PricingEngine
// quote (same engine as Step 6) so the number never jumps. The lightweight
// customTourPricingSummary estimate only shows on the pure manual path.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client/react';
import {
  SUBMIT_CUSTOM_TOUR_REQUEST,
  GET_EXPERIENCES_BY_COUNTRY,
  GET_EXPERIENCE_CATEGORIES,
  GET_PROPERTIES_BY_COUNTRY,
  GET_BOARD_BASIS_OPTIONS,
  GET_TRANSFER_TYPES,
  GET_TRAVEL_PACES,
  GET_INSURANCE_PLANS,
  GET_DIETARY_OPTIONS,
  GET_OPERATOR_SEASONS,
  GET_CUSTOM_TOUR_PRICING_SUMMARY,
  GET_CUSTOM_TOUR_PRICING_PREVIEW,
  GET_TOUR_FOR_CUSTOMISE,
} from '../graphql/tourBuilder';
import { TouristComposer } from '../components/TouristComposer';
import StepParksLodgesExperiences from '../components/StepParksLodgesExperiences';
import SIORecommendations, { SIOAppliedBanner } from '../components/SIORecommendations';
import {
  PricingBreakdown,
  useServerPricingLines,
  useSummaryPricingLines,
} from '../components/PricingBreakdown';

import {
  ArrowLeft, CheckCircle2, Sparkles, Users, Hotel,
  Utensils, Camera, Mountain, Waves, Building2,
  Shield, Clock, CreditCard, MapPin, Car, Calendar,
  Heart, Coffee, Sun, Moon, Zap, AlertCircle, Check,
  ChevronDown, Info, ArrowRight, Globe, Plane, Phone,
  Loader2, X,
} from 'lucide-react';

/* ─── Design tokens ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.15)';
const NAVY     = '#080e1a';

/* ─── Icon registries ─── */
const MEAL_ICON_MAP     = { Moon, Coffee, Utensils, Sparkles };
const TRANSFER_ICON_MAP = { Users, Car, Sparkles, Globe };
const PACE_ICON_MAP     = { Moon, Sun, Camera, Zap };
const CATEGORY_ICONS    = {
  wildlife: Mountain, nature: Mountain, adventure: Zap,
  cultural: Building2, gastronomy: Utensils, leisure: Waves,
  wellness: Heart, photography: Camera, personal: Sparkles,
};

const getIcon = (map, name, fallback) => map[name] || fallback;

/* ─── Country accent colours ─── */
const COUNTRY_ACCENT = {
  tanzania: '#c2813a', kenya: '#b85c38', italy: '#2d6a4f',
  france: '#003087', japan: '#9b2335', iceland: '#1a5276',
};
const getCountryColor = (name) =>
  COUNTRY_ACCENT[(name || '').toLowerCase()] || GOLD;

// Zone color map for suggestion display (mirrors ZONE_UI_META in StepParksLodgesExperiences)
const ZONE_COLORS = {
  southern: '#f59e0b',
  western:  '#3b82f6',
  northern: '#ef4444',
  central:  GOLD,
};

/* ─── Map API property → UI shape ─── */
const mapApiProperty = (prop) => {
  const cheapestRoom = prop.roomTypes?.length > 0
    ? [...prop.roomTypes].sort((a, b) => a.pricePerNight - b.pricePerNight)[0]
    : null;
  return {
    id:           prop.id,
    name:         prop.name,
    slug:         prop.slug,
    // "Luxury · Tented Camp" — the level is how it's sold, the type what it is.
    tier:         [prop.levelLabel, prop.propertyType?.name].filter(Boolean).join(' · '),
    // The trip's accommodation level comes from the lodge's LEVEL (budget /
    // standard / comfort / luxury — the same slugs packages use), not from
    // its building type: "tented-camp" was being sent as a level.
    tierSlug:     prop.level || '',
    tierIcon:     prop.propertyType?.icon || '',
    stars:        Math.round(prop.averageRating || 0),
    desc:         prop.shortDescription || '',
    image:        prop.featuredImage || '',
    price:        cheapestRoom ? parseFloat(cheapestRoom.pricePerNight) : 0,
    unit:         '/room/night',
    perks:        Array.isArray(prop.amenities) ? prop.amenities.slice(0, 5) : [],
    featured:     prop.isFeatured || false,
    parkId:       prop.park?.id   || null,
    parkName:     prop.park?.name || '',
    parkSlug:     prop.park?.slug || '',
    locationType: (prop.locationType || '').toLowerCase(),
    dbId:         prop.id,
  };
};

/* ─── Map API experience → UI shape ─── */
const mapApiExperience = (exp) => ({
  id:             exp.id,
  name:           exp.name,
  icon:           CATEGORY_ICONS[exp.category?.slug] || Mountain,
  category:       exp.category?.name || 'Experience',
  categorySlug:   exp.category?.slug || '',
  price:          parseFloat(exp.pricePerPerson) || 0,
  duration:       exp.durationDisplay || '',
  difficulty:     exp.difficulty
    ? exp.difficulty.charAt(0).toUpperCase() + exp.difficulty.slice(1).toLowerCase()
    : 'Easy',
  desc:           exp.shortDescription || exp.description || '',
  parkSlug:       exp.park?.slug || '',
  parkName:       exp.park?.name || '',
  parkId:         exp.park?.id   || null,
  priceOnRequest: exp.priceOnRequest || false,
  dbId:           exp.id,
});

/* ─── Resolve operator season for a date ─── */
const resolveSeasonForDate = (seasons, date) => {
  if (!seasons?.length || !date) return null;
  const d     = new Date(date);
  const month = d.getMonth() + 1;
  for (const season of seasons) {
    for (const ov of season.overrides || []) {
      const from = new Date(ov.dateFrom);
      const to   = new Date(ov.dateTo);
      if (d >= from && d <= to) return { ...season, isOverride: true };
    }
  }
  for (const season of seasons) {
    const { monthFrom, monthTo } = season;
    let inRange;
    if (monthFrom <= monthTo) {
      inRange = month >= monthFrom && month <= monthTo;
    } else {
      inRange = month >= monthFrom || month <= monthTo;
    }
    if (inRange) return season;
  }
  return null;
};

/* ─── Debounce hook ─── */
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ─── Styles ─── */
const BuilderStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');
    .f-cormorant { font-family: 'Cormorant Garamond', serif; }
    .f-jost      { font-family: 'Jost', sans-serif; }
    .grain { background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E"); }
    .prog-bar { height: 2px; transition: background 0.6s; }
    .prog-bar.done    { background: ${GOLD}; }
    .prog-bar.active  { background: ${NAVY}; }
    .prog-bar.pending { background: #e2ddd6; }
    .sel-card { border: 1px solid #e2ddd6; background: #fff; cursor: pointer; transition: all 0.3s cubic-bezier(0.22,1,0.36,1); }
    .sel-card:hover { border-color: #b0aaa0; transform: translateY(-1px); }
    .sel-card.selected { border-color: ${GOLD}; background: ${GOLD_DIM}; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(184,151,90,0.15); }
    .acc-option-card { border: 1px solid #e2ddd6; background: #fff; cursor: pointer; transition: all 0.3s cubic-bezier(0.22,1,0.36,1); position: relative; overflow: hidden; }
    .acc-option-card:hover { border-color: #b0aaa0; transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,0.07); }
    .acc-option-card.selected { border-color: ${GOLD}; background: ${GOLD_DIM}; transform: translateY(-3px); box-shadow: 0 10px 32px rgba(184,151,90,0.18); }
    .acc-option-card .featured-badge { position: absolute; top: 0; right: 0; background: ${GOLD}; color: ${NAVY}; font-family: 'Jost', sans-serif; font-size:12px; font-weight: 700; letter-spacing:0.16em; text-transform: uppercase; padding: 4px 10px; }
    .acc-option-card .image-strip { aspect-ratio: 16/9; background-size: cover; background-position: center; position: relative; overflow: hidden; }
    .acc-option-card .image-strip::after { content: ''; position: absolute; inset: 0; background: linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.35) 100%); }
    .meal-mode-toggle { display: flex; border: 1px solid #e2ddd6; overflow: hidden; width: fit-content; }
    .meal-mode-btn { display: flex; align-items: center; gap: 7px; padding: 10px 20px; cursor: pointer; transition: all 0.25s; font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600; letter-spacing:0.16em; text-transform: uppercase; color:#12100e; background: #fff; border: none; }
    .meal-mode-btn:hover { color: ${NAVY}; background: #f8f7f5; }
    .meal-mode-btn.active { background: ${NAVY}; color: #fff; }
    .meal-chip { display: flex; align-items: center; gap: 8px; padding: 9px 14px; border: 1px solid #e2ddd6; background: #fff; cursor: pointer; transition: all 0.22s; }
    .meal-chip:hover { border-color: #b0aaa0; }
    .meal-chip.active { border-color: ${GOLD}; background: ${GOLD_DIM}; }
    .qty-btn { width: 36px; height: 36px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd9d2; background: #fff; color:#12100e; cursor: pointer; transition: all 0.2s; font-size:18px; }
    .qty-btn:hover { border-color: ${GOLD}; color: ${NAVY}; }
    .qty-btn:disabled { opacity: 0.3; cursor: default; }
    .builder-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 1000; transition: all 0.4s cubic-bezier(0.22,1,0.36,1); }
    .builder-nav.scrolled { background: rgba(245,243,239,0.95); backdrop-filter: blur(16px); border-bottom: 1px solid #e2ddd6; }
    .primary-cta { position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; gap: 10px; font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600; letter-spacing:0.16em; text-transform: uppercase; padding: 16px 32px; background: ${NAVY}; color: #fff; border: 1px solid ${NAVY}; cursor: pointer; transition: all 0.35s cubic-bezier(0.22,1,0.36,1); }
    .primary-cta::before { content: ''; position: absolute; inset: 0; background: ${GOLD}; transform: scaleX(0); transform-origin: left; transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); z-index: 0; }
    .primary-cta:hover::before { transform: scaleX(1); }
    .primary-cta:hover { color: ${NAVY}; border-color: ${GOLD}; }
    .primary-cta span, .primary-cta svg { position: relative; z-index: 1; }
    .ghost-cta { display: flex; align-items: center; justify-content: center; gap: 8px; font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600; letter-spacing:0.16em; text-transform: uppercase; padding: 16px 24px; border: 1px solid #ddd9d2; color:#12100e; background: #fff; cursor: pointer; transition: all 0.3s; }
    .ghost-cta:hover { border-color: ${NAVY}; color: ${NAVY}; }
    .tag-badge { font-family: 'Jost', sans-serif; font-size:12px; font-weight: 600; letter-spacing:0.16em; text-transform: uppercase; padding: 4px 10px; background: ${GOLD_DIM}; border: 1px solid rgba(184,151,90,0.3); color: ${GOLD}; display: inline-flex; align-items: center; }
    .toggle-opt { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border: 1px solid #e2ddd6; background: #fff; cursor: pointer; font-family: 'Jost', sans-serif; font-size:13px; font-weight: 500; letter-spacing: 0.15em; text-transform: uppercase; color:#12100e; transition: all 0.25s; }
    .toggle-opt:hover { border-color: #b0aaa0; color: ${NAVY}; }
    .toggle-opt.active { border-color: ${GOLD}; background: ${GOLD_DIM}; color: ${NAVY}; }
    .timeline-leg { position: relative; padding-left: 40px; }
    .timeline-leg::before { content: ''; position: absolute; left: 11px; top: 28px; bottom: -8px; width: 1px; background: linear-gradient(to bottom, ${GOLD}, rgba(184,151,90,0.1)); }
    .timeline-leg:last-child::before { display: none; }
    .timeline-dot { position: absolute; left: 0; top: 18px; width: 24px; height: 24px; border: 1px solid ${GOLD}; background: #F5F3EF; display: flex; align-items: center; justify-content: center; }
    .pace-opt { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 14px 10px; border: 1px solid #e2ddd6; background: #fff; cursor: pointer; transition: all 0.25s; text-align: center; }
    .pace-opt:hover { border-color: #b0aaa0; }
    .pace-opt.active { border-color: ${GOLD}; background: ${GOLD_DIM}; }
    .ins-card { border: 1px solid #e2ddd6; background: #fff; transition: all 0.3s; cursor: pointer; }
    .ins-card:hover { border-color: #b0aaa0; }
    .ins-card.selected { border-color: ${GOLD}; background: ${GOLD_DIM}; }
    @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
    .fade-up { animation: fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both; }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .fade-in { animation: fadeIn 0.35s ease both; }
    @keyframes popIn { 0% { transform: scale(0.6); opacity: 0; } 70% { transform: scale(1.08); } 100% { transform: scale(1); opacity: 1; } }
    .pop-in { animation: popIn 0.6s cubic-bezier(0.22,1,0.36,1) both; }
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { scrollbar-width: none; }
    .stagger > *:nth-child(1) { animation-delay: 0ms; }
    .stagger > *:nth-child(2) { animation-delay: 60ms; }
    .stagger > *:nth-child(3) { animation-delay: 120ms; }
    .stagger > *:nth-child(4) { animation-delay: 180ms; }
    .stagger > *:nth-child(5) { animation-delay: 240ms; }
    .stagger > *:nth-child(6) { animation-delay: 300ms; }
    .review-expand { overflow: hidden; transition: max-height 0.5s cubic-bezier(0.22,1,0.36,1), opacity 0.3s; }
    .review-expand.open   { max-height: 900px; opacity: 1; }
    .review-expand.closed { max-height: 0; opacity: 0; }
    .preview-skeleton { background: linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; height: 16px; border-radius: 2px; }
    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .tooltip-wrap { position: relative; }
    .tooltip-wrap:hover .tooltip-box { opacity: 1; pointer-events: auto; }
    .tooltip-box { position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%); background: ${NAVY}; color: #fff; padding: 8px 12px; white-space: nowrap; font-family: 'Jost', sans-serif; font-size:13px; opacity: 0; pointer-events: none; transition: opacity 0.2s; z-index: 100; }
    .tooltip-box::after { content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%); border: 5px solid transparent; border-top-color: ${NAVY}; }
  `}</style>
);

/* ─── Shared sub-components ─── */
const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-3">
    <div className="w-6 h-px" style={{ background: GOLD }} />
    <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>{text}</span>
  </div>
);

const StepHeading = ({ sub, title }) => (
  <div className="mb-8">
    <SectionLabel text={sub} />
    <h2 className="f-cormorant text-slate-900 font-light leading-none" style={{ fontSize: 'clamp(32px,3.5vw,40px)' }}>
      {title}
    </h2>
  </div>
);

const Tooltip = ({ text, children }) => (
  <div className="tooltip-wrap inline-flex">
    {children}
    <div className="tooltip-box">{text}</div>
  </div>
);

/* ─── AccommodationCard ─── */
const AccommodationCard = ({ opt, isSelected, onSelect, accentColor, nights }) => (
  <button
    onClick={() => onSelect(opt.id)}
    className={`acc-option-card text-left fade-up ${isSelected ? 'selected' : ''}`}
  >
    {opt.featured && <div className="featured-badge">Most Popular</div>}
    <div className="image-strip" style={{
      backgroundImage:  opt.image ? `url(${opt.image})` : 'none',
      backgroundColor: !opt.image ? '#f0ece6' : 'transparent',
    }}>
      {isSelected && (
        <div className="absolute top-2 left-2 z-10 w-6 h-6 flex items-center justify-center"
          style={{ background: accentColor }}>
          <Check size={11} strokeWidth={2.5} style={{ color: '#fff' }} />
        </div>
      )}
      {opt.tier && (
        <div className="absolute bottom-2 left-3 z-10">
          <span className="f-jost text-[12px] font-semibold uppercase tracking-[0.16em] px-2 py-1"
            style={{ background: isSelected ? accentColor : 'rgba(0,0,0,0.5)', color: '#fff' }}>
            {opt.tier}
          </span>
        </div>
      )}
      {opt.stars > 0 && (
        <div className="absolute bottom-2 right-3 z-10">
          <span className="f-jost text-[13px]" style={{ color: GOLD }}>
            {'★'.repeat(Math.min(opt.stars, 5))}
          </span>
        </div>
      )}
    </div>
    <div className="p-5">
      <h4 className="f-cormorant text-slate-900 font-light text-[25px] leading-none mb-1">{opt.name}</h4>
      <p className="f-jost text-[13px] text-slate-500 font-light leading-relaxed mb-4">{opt.desc}</p>
      {opt.perks?.length > 0 && (
        <div className="space-y-1 mb-4">
          {opt.perks.slice(0, 3).map(p => (
            <div key={p} className="flex items-center gap-2">
              <div className="w-1 h-1 rounded-full flex-shrink-0"
                style={{ background: isSelected ? accentColor : '#cbd5e1' }} />
              <span className="f-jost text-[13px] text-slate-600">{p}</span>
            </div>
          ))}
          {opt.perks.length > 3 && (
            <p className="f-jost text-[13px]" style={{ color: accentColor }}>
              +{opt.perks.length - 3} more inclusions
            </p>
          )}
        </div>
      )}
      <div className="flex items-baseline gap-2 pt-3 border-t" style={{ borderColor: '#f0ece6' }}>
        <span className="f-cormorant text-slate-900 font-light text-[32px] leading-none">
          ${opt.price.toLocaleString()}
        </span>
        <span className="f-jost text-[13px] text-slate-400">{opt.unit}</span>
        {opt.price > 0 && nights > 0 && (
          <span className="f-jost text-[13px] ml-auto" style={{ color: '#12100e' }}>
            ≈ ${(opt.price * nights).toLocaleString()} for {nights} nights
          </span>
        )}
      </div>
    </div>
  </button>
);

/* ══════════════════════════════════════════════
   TOUR BUILDER PAGE
══════════════════════════════════════════════ */
const TourBuilderPage = () => {
  const location   = useLocation();
  const navigate   = useNavigate();
  const sidebarRef = useRef(null);

  const locationState = location.state || {};
  const searchData = useMemo(() => {
    return locationState?.searchData || { countries: [], startDate: null, endDate: null };
  }, [locationState?.searchData]);
  const user = locationState.user || { name: '', email: '', phone: '' };
  // Started from a ready-made safari ("Customise this safari"): its stays
  // seed the builder; the guest changes what they like.
  // Also reachable as /tour-builder?from=<safari-slug> — a link that can be
  // shared, emailed or reloaded, unlike navigation state.
  const fromParam = new URLSearchParams(location.search).get('from');
  const fromTour = locationState.fromTour || (fromParam ? { slug: fromParam } : null);

  /* Dates are calendar-scoped: a builder reopened from an old tab or link
     can carry dates that have already passed (or none at all). The guest
     picks new ones here — tomorrow at the earliest — before building; the
     server refuses past dates either way. */
  const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const tomorrowISO = (() => { const t = new Date(); t.setDate(t.getDate() + 1); return isoLocal(t); })();
  const maxISO = (() => { const t = new Date(); t.setFullYear(t.getFullYear() + 2); return isoLocal(t); })();
  const startISO = searchData.startDate ? isoLocal(new Date(searchData.startDate)) : null;
  const datesNeedFixing = !startISO || startISO < tomorrowISO;
  const [newDates, setNewDates] = useState({ start: '', end: '' });
  const applyNewDates = () => {
    if (!newDates.start || !newDates.end || newDates.end < newDates.start) return;
    navigate(location.pathname + location.search, {
      replace: true,
      state: {
        ...locationState,
        searchData: {
          ...searchData,
          startDate: new Date(`${newDates.start}T00:00:00`),
          endDate: new Date(`${newDates.end}T00:00:00`),
        },
      },
    });
  };

  /* ─── UI state ─── */
  const [step,               setStep]               = useState(1);
  const [isScrolled,         setIsScrolled]         = useState(false);
  const [isSubmitted,        setIsSubmitted]        = useState(false);
  const [submitError,        setSubmitError]        = useState(null);
  const [isSubmitting,       setIsSubmitting]       = useState(false);
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);

  /* ─── Trip state ─── */
  const [itinerary, setItinerary] = useState([]);
  const [tourists, setTourists] = useState([{ touristType: 'adult', nationality: '' }]);

  useEffect(() => {
    setTourists([{ touristType: 'adult', nationality: '' }]);
  }, [searchData.startDate, searchData.endDate]);

  const groupSize = tourists.length;

  const [accommodation,        setAccommodation]        = useState({});
  const [propertiesCache,      setPropertiesCache]      = useState({});
  const [customAccommodations, setCustomAccommodations] = useState({});

  /* ─── Serengeti zones ─── */
  const [selectedSerengetiZones, setSelectedSerengetiZones] = useState(undefined);

  /* ─── Meal plan ─── */
  const [mealMode,            setMealMode]            = useState('global');
  const [globalMealPlan,      setGlobalMealPlan]      = useState('');
  const [perCountryMeal,      setPerCountryMeal]      = useState({});
  const [expandedMealCountry, setExpandedMealCountry] = useState(null);

  /* ─── Other steps ─── */
  const [transferType,        setTransferType]        = useState('');
  const [pace,                setPace]                = useState('');
  const [insurance,           setInsurance]           = useState('');
  const [flightAssistance,    setFlightAssistance]    = useState(false);
  const [specialRequests,     setSpecialRequests]     = useState('');
  const [dietaryRequirements, setDietaryRequirements] = useState([]);
  const [accessibilityNeeds,  setAccessibilityNeeds]  = useState('');
  const [guestPhone,          setGuestPhone]          = useState(user.phone || '');
  // Asked for here when the guest didn't come through the search drawer
  // (e.g. "Customise this safari" from a tour page).
  const [guestName,           setGuestName]           = useState(user.name || '');
  const [guestEmail,          setGuestEmail]          = useState(user.email || '');
  const [customMoments,       setCustomMoments]       = useState({});

  /* ─── Step 2: Park + lodge selections ─── */
  const [selectedParks, setSelectedParks] = useState({});
  const [parkNights,    setParkNights]    = useState({});
  const [parkLodges,    setParkLodges]    = useState({});

  /* ─── SIO recommendation state ─── */
  const [sioMode, setSioMode] = useState('gallery');   // 'gallery' | 'applied' | 'manual'
  const [appliedRecommendation, setAppliedRecommendation] = useState(null);

  const [visitedSteps,   setVisitedSteps]   = useState(new Set([1]));
  const [expandedReview, setExpandedReview] = useState({ 0: true });

  const TOTAL_STEPS = 7;
  const stepLabels  = [
    'Duration', 'Experiences',
    'Meal Plan', 'Transfers', 'Extras',
    'Pricing Preview', 'Review',
  ];

  /* ─── Scroll listener ─── */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ─── Initialise itinerary ─── */
  useEffect(() => {
    if (searchData.countries?.length > 0) {
      const totalNights = (() => {
        if (searchData.startDate && searchData.endDate) {
          const diff = Math.round(
            (new Date(searchData.endDate) - new Date(searchData.startDate))
            / (1000 * 60 * 60 * 24)
          );
          return diff > 0 ? diff : searchData.countries.length * 3;
        }
        return searchData.countries.length * 3;
      })();
      const baseNights      = Math.floor(totalNights / searchData.countries.length);
      const remainderNights = totalNights % searchData.countries.length;
      setItinerary(
        searchData.countries.map((c, idx) => ({
          name: c,
          nights: baseNights + (idx === 0 ? remainderNights : 0),
          selectedAttractions: [],
        }))
      );
      const mealDefaults = {};
      searchData.countries.forEach(c => { mealDefaults[c] = ''; });
      setPerCountryMeal(mealDefaults);
      setExpandedMealCountry(searchData.countries[0]);
    }
  }, [searchData.countries, searchData.startDate, searchData.endDate]);

  /* ─── Reference data queries ─── */
  const { data: boardBasisData      } = useQuery(GET_BOARD_BASIS_OPTIONS,    { fetchPolicy: 'cache-first' });
  const { data: transferData        } = useQuery(GET_TRANSFER_TYPES,         { fetchPolicy: 'cache-first' });
  const { data: paceData            } = useQuery(GET_TRAVEL_PACES,           { fetchPolicy: 'cache-first' });
  const { data: insuranceData       } = useQuery(GET_INSURANCE_PLANS,        { fetchPolicy: 'cache-first' });
  const { data: dietaryData         } = useQuery(GET_DIETARY_OPTIONS,        { fetchPolicy: 'cache-first' });
  const { data: categoriesData      } = useQuery(GET_EXPERIENCE_CATEGORIES,  { fetchPolicy: 'cache-first' });
  const { data: operatorSeasonsData } = useQuery(GET_OPERATOR_SEASONS,       { fetchPolicy: 'cache-first' });

  /* ─── Derived reference arrays ─── */
  const mealPlans = useMemo(() =>
    (boardBasisData?.boardBasisOptions || []).map(b => ({
      id:        b.slug,
      name:      b.name,
      desc:      b.description,
      price:     parseFloat(b.priceSupplementPerPersonPerNight || 0),
      icon:      getIcon(MEAL_ICON_MAP, b.icon, Utensils),
      dbId:      b.id,
      isDefault: b.isDefault,
    })), [boardBasisData]);

  const transfers = useMemo(() =>
    (transferData?.transferTypes || []).map(t => ({
      id:        t.slug,
      name:      t.name,
      desc:      t.description,
      price:     parseFloat(t.pricePerNight || 0),
      icon:      getIcon(TRANSFER_ICON_MAP, t.icon, Car),
      dbId:      t.id,
      isDefault: t.isDefault,
    })), [transferData]);

  const paceOptions = useMemo(() =>
    (paceData?.travelPaces || []).map(p => ({
      id:        p.slug,
      name:      p.name,
      desc:      p.description,
      sub:       p.subLabel,
      icon:      getIcon(PACE_ICON_MAP, p.icon, Sun),
      dbId:      p.id,
      isDefault: p.isDefault,
    })), [paceData]);

  const totalNights = itinerary.reduce((s, l) => s + l.nights, 0);

  const insuranceOptions = useMemo(() =>
    (insuranceData?.insurancePlans || []).map(i => {
      let cover = i.coverageItems || [];
      if (typeof cover === 'string') { try { cover = JSON.parse(cover); } catch { cover = []; } }
      return {
        id:    i.slug,
        name:  i.name,
        desc:  i.description,
        price: parseFloat(i.ratePerPersonPerDay || 0) * (totalNights || 7),
        cover: Array.isArray(cover) ? cover : [],
        dbId:  i.id,
      };
    }), [insuranceData, totalNights]);

  const experienceCategories = useMemo(() =>
    (categoriesData?.experienceCategories || []).map(c => ({
      id: c.id, name: c.name, slug: c.slug, icon: c.icon,
    })), [categoriesData]);

  const operatorSeasons = useMemo(() =>
    operatorSeasonsData?.operatorSeasons || [], [operatorSeasonsData]);

  /* ─── Set defaults from DB ─── */
  useEffect(() => {
    if (mealPlans.length > 0 && !globalMealPlan) {
      const def = mealPlans.find(m => m.isDefault) || mealPlans[0];
      setGlobalMealPlan(def.id);
      setPerCountryMeal(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(c => { if (!updated[c]) updated[c] = def.id; });
        return updated;
      });
    }
  }, [mealPlans, globalMealPlan]);

  useEffect(() => {
    if (transfers.length > 0 && !transferType) {
      const def = transfers.find(t => t.isDefault) || transfers[0];
      setTransferType(def.id);
    }
  }, [transfers, transferType]);

  useEffect(() => {
    if (paceOptions.length > 0 && !pace) {
      const def = paceOptions.find(p => p.isDefault) || paceOptions[0];
      setPace(def.id);
    }
  }, [paceOptions, pace]);

  /* ─── Country pre-fetches (Rules of Hooks — max 5) ─── */
  const _s0 = itinerary[0]?.name.toLowerCase().replace(/\s+/g, '-') || '';
  const _s1 = itinerary[1]?.name.toLowerCase().replace(/\s+/g, '-') || '';
  const _s2 = itinerary[2]?.name.toLowerCase().replace(/\s+/g, '-') || '';
  const _s3 = itinerary[3]?.name.toLowerCase().replace(/\s+/g, '-') || '';
  const _s4 = itinerary[4]?.name.toLowerCase().replace(/\s+/g, '-') || '';

  const { data: _ed0, loading: _el0 } = useQuery(GET_EXPERIENCES_BY_COUNTRY, { variables: { countrySlug: _s0 }, fetchPolicy: 'cache-first', skip: !_s0 });
  const { data: _ed1, loading: _el1 } = useQuery(GET_EXPERIENCES_BY_COUNTRY, { variables: { countrySlug: _s1 }, fetchPolicy: 'cache-first', skip: !_s1 });
  const { data: _ed2, loading: _el2 } = useQuery(GET_EXPERIENCES_BY_COUNTRY, { variables: { countrySlug: _s2 }, fetchPolicy: 'cache-first', skip: !_s2 });
  const { data: _ed3, loading: _el3 } = useQuery(GET_EXPERIENCES_BY_COUNTRY, { variables: { countrySlug: _s3 }, fetchPolicy: 'cache-first', skip: !_s3 });
  const { data: _ed4, loading: _el4 } = useQuery(GET_EXPERIENCES_BY_COUNTRY, { variables: { countrySlug: _s4 }, fetchPolicy: 'cache-first', skip: !_s4 });
  const _expData    = [_ed0, _ed1, _ed2, _ed3, _ed4];
  const _expLoading = [_el0, _el1, _el2, _el3, _el4];

  const [_fetchPd0, { data: _pd0, loading: _pl0 }] = useLazyQuery(GET_PROPERTIES_BY_COUNTRY, { fetchPolicy: 'network-only' });
  const [_fetchPd1, { data: _pd1, loading: _pl1 }] = useLazyQuery(GET_PROPERTIES_BY_COUNTRY, { fetchPolicy: 'network-only' });
  const [_fetchPd2, { data: _pd2, loading: _pl2 }] = useLazyQuery(GET_PROPERTIES_BY_COUNTRY, { fetchPolicy: 'network-only' });
  const [_fetchPd3, { data: _pd3, loading: _pl3 }] = useLazyQuery(GET_PROPERTIES_BY_COUNTRY, { fetchPolicy: 'network-only' });
  const [_fetchPd4, { data: _pd4, loading: _pl4 }] = useLazyQuery(GET_PROPERTIES_BY_COUNTRY, { fetchPolicy: 'network-only' });
  const _propFetchers = [_fetchPd0, _fetchPd1, _fetchPd2, _fetchPd3, _fetchPd4];
  const _propLoading  = useMemo(() => [_pl0, _pl1, _pl2, _pl3, _pl4], [_pl0, _pl1, _pl2, _pl3, _pl4]);

  /* ─── Fire property queries on Step 2 entry ─── */
  useEffect(() => {
    if (step !== 2) return;
    itinerary.forEach((leg, idx) => {
      if (idx >= 5) return;
      const slug = leg.name.toLowerCase().replace(/\s+/g, '-');
      if (!slug) return;
      const experienceIds = leg.selectedAttractions
        .filter(a => !a.isCustom && a.dbId)
        .map(a => String(a.dbId));
      _propFetchers[idx]({
        variables: {
          countrySlug:   slug,
          experienceIds: experienceIds.length > 0 ? experienceIds : undefined,
        },
      });
    });
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Populate propertiesCache ─── */
  useEffect(() => {
    const newCache = {};
    itinerary.forEach((leg, idx) => {
      const data = [_pd0, _pd1, _pd2, _pd3, _pd4][idx];
      if (data?.propertiesByCountry) {
        newCache[leg.name] = data.propertiesByCountry.map(mapApiProperty);
      }
    });
    if (Object.keys(newCache).length > 0) {
      setPropertiesCache(prev => ({ ...prev, ...newCache }));
    }
  }, [_pd0, _pd1, _pd2, _pd3, _pd4]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Auto-select first property when cache loads ─── */
  useEffect(() => {
    const updates = {};
    itinerary.forEach(leg => {
      if (!accommodation[leg.name]) {
        const props = propertiesCache[leg.name] || [];
        if (props.length > 0) updates[leg.name] = props[0].id;
      }
    });
    if (Object.keys(updates).length > 0) {
      setAccommodation(prev => ({ ...prev, ...updates }));
    }
  }, [propertiesCache, accommodation, itinerary]);

  /* ─── Derived helpers ─── */
  const getCountryProperties = useCallback(
    (name) => propertiesCache[name] || [],
    [propertiesCache]
  );

  const getMealForCountry = useCallback(
    (country) => mealMode === 'global'
      ? globalMealPlan
      : (perCountryMeal[country] || globalMealPlan),
    [mealMode, globalMealPlan, perCountryMeal]
  );

  const getMealPlanObj = useCallback(
    (id) => mealPlans.find(m => m.id === id) || mealPlans[0] || { id: '', name: '', desc: '', price: 0, icon: Utensils },
    [mealPlans]
  );

  const totalAttractions = itinerary.reduce((s, l) => s + l.selectedAttractions.length, 0);

  const totalNightsFromDates = useMemo(() => {
    if (searchData.startDate && searchData.endDate) {
      const diff = Math.round(
        (new Date(searchData.endDate) - new Date(searchData.startDate))
        / (1000 * 60 * 60 * 24)
      );
      return diff > 0 ? diff : itinerary.reduce((s, l) => s + l.nights, 0);
    }
    return itinerary.reduce((s, l) => s + l.nights, 0);
  }, [searchData.startDate, searchData.endDate, itinerary]);

  /* ─── Derived IDs ─── */
  const derivedAccommodationLevelSlug = useMemo(() => {
    for (const leg of itinerary) {
      const props = getCountryProperties(leg.name);
      const sel = props.find(o => o.id === accommodation[leg.name]);
      if (sel?.tierSlug) return sel.tierSlug;
    }
    return null;
  }, [itinerary, accommodation, propertiesCache]); // eslint-disable-line

  const derivedSelectedParkIds = useMemo(() => {
    const ids = new Set();
    Object.values(selectedParks).forEach(parkList => {
      (parkList || []).forEach(p => { if (p.id) ids.add(p.id); });
    });
    if (ids.size === 0) {
      itinerary.forEach(leg => {
        const props = getCountryProperties(leg.name);
        const sel = props.find(o => o.id === accommodation[leg.name]);
        if (sel?.parkId) ids.add(sel.parkId);
      });
    }
    return [...ids];
  }, [selectedParks, itinerary, accommodation, propertiesCache]); // eslint-disable-line

  const parkNightsJson = useMemo(() => {
    const result = {};
    Object.values(selectedParks).forEach(parkList => {
      (parkList || []).forEach(park => {
        const nights = parkNights[park.id];
        if (nights) result[park.id] = nights;
      });
    });
    itinerary.forEach(leg => {
      const legParks = selectedParks[leg.name] || [];
      if (legParks.length === 0) return;
      const filled = legParks.filter(p => result[p.id]);
      if (filled.length === 0) {
        const perPark = Math.max(1, Math.floor(leg.nights / legParks.length));
        legParks.forEach(p => { result[p.id] = perPark; });
      }
    });
    return Object.keys(result).length > 0 ? JSON.stringify(result) : undefined;
  }, [selectedParks, parkNights, itinerary]);

  const migrationZonesJson = useMemo(() => {
    if (!Array.isArray(selectedSerengetiZones) || selectedSerengetiZones.length === 0)
      return undefined;
    const result = {};
    Object.values(selectedParks).forEach(parkList => {
      (parkList || []).forEach(park => {
        const slug = (park.slug || '').toLowerCase();
        const name = (park.name || '').toLowerCase();
        if (slug.includes('serengeti') || name.includes('serengeti')) {
          result[park.id] = selectedSerengetiZones.map(z => z.key);
        }
      });
    });
    return Object.keys(result).length > 0 ? JSON.stringify(result) : undefined;
  }, [selectedParks, selectedSerengetiZones]);

  const selectedPropertyIds = useMemo(() =>
    Object.values(accommodation).filter(Boolean),
    [accommodation]
  );

  const touristCompositionInput = useMemo(() =>
    tourists.map(t => ({
      touristType: t.touristType,
      nationality: t.nationality || 'INT',
    })),
    [tourists]
  );

  const locationName = useMemo(() => {
    const zoneNames = Array.isArray(selectedSerengetiZones)
      ? selectedSerengetiZones.map(z => z.label).filter(Boolean)
      : [];
    const propNames = Object.values(propertiesCache)
      .flat()
      .filter(p => Object.values(accommodation).includes(p.id))
      .map(p => p.name);
    return [...zoneNames, ...propNames].join(' ');
  }, [selectedSerengetiZones, propertiesCache, accommodation]);

  /* ─── Summary pricing query ─── */
  const summaryReady = useMemo(() => {
    const hasNationality = tourists.some(t => t.nationality?.length > 0);
    return (
      hasNationality &&
      !!searchData.startDate &&
      !!searchData.endDate &&
      searchData.countries.length > 0
    );
  }, [tourists, searchData]);

  const debouncedSummaryVars = useDebounce({
    countrySlugs:        searchData.countries.map(c => c.toLowerCase().replace(/\s+/g, '-')),
    touristComposition:  touristCompositionInput,
    startDate:           searchData.startDate
      ? new Date(searchData.startDate).toISOString().split('T')[0] : null,
    endDate:             searchData.endDate
      ? new Date(searchData.endDate).toISOString().split('T')[0] : null,
    selectedPropertyIds: selectedPropertyIds.length > 0 ? selectedPropertyIds : undefined,
    transferTypeId:      transfers.find(t => t.id === transferType)?.dbId || undefined,
    insuranceId:         insuranceOptions.find(i => i.id === insurance)?.dbId || undefined,
    parkNightsJson,
    migrationZonesJson,
  }, 800);

  const { data: summaryData, loading: summaryLoading } = useQuery(
    GET_CUSTOM_TOUR_PRICING_SUMMARY,
    { variables: debouncedSummaryVars, skip: !summaryReady, fetchPolicy: 'network-only' }
  );

  const summary = summaryData?.customTourPricingSummary || null;

  /* ─── Full preview query (Step 6) ─── */
  const [fetchPreview, { loading: previewQueryLoading, data: previewQueryData }] = useLazyQuery(
    GET_CUSTOM_TOUR_PRICING_PREVIEW, { fetchPolicy: 'network-only' }
  );

  const [previewDataState, setPreviewDataState] = useState(null);

  useEffect(() => {
    if (previewQueryData) {
      setPreviewDataState(previewQueryData.customTourPricingPreview || null);
    }
  }, [previewQueryData]);

  useEffect(() => {
    if (step !== 6) return;
    if (selectedPropertyIds.length === 0) return;

    const selectedExperienceIds = itinerary.flatMap(leg =>
      leg.selectedAttractions
        .filter(a => !a.isCustom && a.dbId)
        .map(a => String(a.dbId))
    );

    const boardBasisSlugs = mealMode === 'global'
      ? (globalMealPlan ? [globalMealPlan] : [])
      : [...new Set(Object.values(perCountryMeal).filter(Boolean))];

    fetchPreview({
      variables: {
        countrySlugs:            searchData.countries.map(c => c.toLowerCase().replace(/\s+/g, '-')),
        touristComposition:      touristCompositionInput,
        startDate:               searchData.startDate
          ? new Date(searchData.startDate).toISOString().split('T')[0] : null,
        endDate:                 searchData.endDate
          ? new Date(searchData.endDate).toISOString().split('T')[0] : null,
        selectedPropertyIds,
        selectedExperienceIds:   selectedExperienceIds.length > 0 ? selectedExperienceIds : undefined,
        selectedParkIds:         derivedSelectedParkIds.length > 0 ? derivedSelectedParkIds : undefined,
        parkNightsJson,
        migrationZonesJson,
        transferTypeId:          transfers.find(t => t.id === transferType)?.dbId || undefined,
        insuranceId:             insuranceOptions.find(i => i.id === insurance)?.dbId || undefined,
        boardBasisSlugs:         boardBasisSlugs.length > 0 ? boardBasisSlugs : undefined,
        includeFlightAssistance: flightAssistance || undefined,
        channel:                 'b2c',
      },
    });
  }, [
    step, selectedPropertyIds, derivedSelectedParkIds, parkNightsJson, migrationZonesJson,
    searchData, touristCompositionInput, transferType, insurance, transfers,
    insuranceOptions, fetchPreview, itinerary, mealMode,
    globalMealPlan, perCountryMeal, flightAssistance,
  ]);

  const activePreviewData = previewDataState;

  /* ─── Pricing lines ─── */
  const serverLines  = useServerPricingLines({ preview: activePreviewData });
  const summaryLines = useSummaryPricingLines({ summary, itinerary, accommodation });

  // Once a blueprint is applied, anchor Steps 1–5 on its PricingEngine quote
  // (same engine as Step 6) instead of the lightweight estimate — so the number
  // the guest saw on the card is the number they keep seeing, converging (not
  // jumping) when the authoritative Step 6 preview lands.
  const usingBlueprintQuote =
    step < 6 && appliedRecommendation?.isPriced && !activePreviewData;

  // Has the guest edited lodges away from the applied blueprint's set?
  const editedSinceBlueprint = useMemo(() => {
    if (!appliedRecommendation?.stays) return false;
    const blueprintPropIds = appliedRecommendation.stays
      .map(s => s.propertyId)
      .filter(Boolean)
      .sort();
    if (blueprintPropIds.length === 0) return false;
    const currentPropIds = [...selectedPropertyIds].sort();
    if (currentPropIds.length !== blueprintPropIds.length) return true;
    return currentPropIds.some((id, i) => id !== blueprintPropIds[i]);
  }, [appliedRecommendation, selectedPropertyIds]);

  const blueprintLines = useMemo(() => {
    if (!usingBlueprintQuote) return null;
    return [
      { type: 'section', label: `${appliedRecommendation.categoryLabel} blueprint`,
        total: appliedRecommendation.totalPrice },
      { type: 'line',
        label: 'Safari package · server-priced',
        sublabel: 'Accommodation · park fees · transfers · guide — final price confirmed at Step 6',
        amount: appliedRecommendation.totalPrice },
    ];
  }, [usingBlueprintQuote, appliedRecommendation]);

  const sidebarLines = step >= 6 && activePreviewData
    ? serverLines
    : (blueprintLines || summaryLines);

  const sidebarTotals = step >= 6 && activePreviewData
    ? {
        tax:       activePreviewData.tax,
        taxLabel:  activePreviewData.taxLabel,
        total:     activePreviewData.totalPrice,
        perPerson: activePreviewData.pricePerPerson,
        currency:  activePreviewData.currency || 'USD',
      }
    : usingBlueprintQuote
      ? {
          tax:       null,
          taxLabel:  'VAT',
          total:     appliedRecommendation.totalPrice,
          perPerson: appliedRecommendation.pricePerPerson,
          currency:  appliedRecommendation.currency || 'USD',
        }
      : {
          tax:       summary?.tax,
          taxLabel:  summary?.taxLabel || 'VAT',
          total:     summary?.totalPrice || 0,
          perPerson: summary?.pricePerPerson,
          currency:  summary?.currency || 'USD',
        };

  // Skeletons should not show while we have a blueprint quote to display
  const showHeroSkeleton    = summaryLoading && !usingBlueprintQuote && step < 6;
  const showSidebarSkeleton = summaryLoading && !summary && step < 6 && !usingBlueprintQuote;

  /* ─── Season hint ─── */
  const getSeasonHint = useCallback((countryName) => {
    if (!searchData.startDate || !operatorSeasons.length) return null;
    return resolveSeasonForDate(operatorSeasons, searchData.startDate);
  }, [searchData.startDate, operatorSeasons]);

  /* ─── Apply an SIO blueprint to builder state ─── */
  const applySIORecommendation = useCallback((rec) => {
    const newSelectedParks = {};
    const newParkNights    = {};
    const newAccommodation = {};
    let   newZones;
    let   hasSerengeti = false;

    (rec.stays || []).forEach(stay => {
      const leg = itinerary.find(l =>
        l.name.toLowerCase().replace(/\s+/g, '-') === (stay.parkCountrySlug || '')
      ) || itinerary[0];
      if (!leg) return;

      if (!newSelectedParks[leg.name]) newSelectedParks[leg.name] = [];
      if (!newSelectedParks[leg.name].find(p => p.id === stay.parkId)) {
        newSelectedParks[leg.name].push({ id: stay.parkId, name: stay.parkName, slug: stay.parkSlug });
      }
      newParkNights[stay.parkId] = stay.nights;
      if (stay.propertyId) newAccommodation[stay.parkId] = stay.propertyId;  // keyed by park.id

      if ((stay.parkSlug || '').includes('serengeti')) {
        hasSerengeti = true;
        if (stay.migrationZones?.length) {
          newZones = stay.migrationZones.map(key => ({
            key,
            label: key.charAt(0).toUpperCase() + key.slice(1) + ' Serengeti',
            color: ZONE_COLORS[key] || GOLD,
          }));
        }
      }
    });

    setSelectedParks(newSelectedParks);
    setParkNights(newParkNights);
    setAccommodation(prev => ({ ...prev, ...newAccommodation }));
    if (newZones !== undefined) setSelectedSerengetiZones(newZones);
    else if (hasSerengeti)      setSelectedSerengetiZones([]);   // “no migration” intent
    setAppliedRecommendation(rec);
    setSioMode('applied');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [itinerary]);

  /* ─── Customise a ready-made safari ─────────────────────────────────────
     1. Load the safari. 2. If no countries came with it, seed them from its
     parks (a replace-navigation — the builder remounts on the new state).
     3. Once the country legs exist, apply its stays as the blueprint, the
     same path an SIO suggestion takes. Once per mount. */
  const { data: baseTourData } = useQuery(GET_TOUR_FOR_CUSTOMISE, {
    variables: { slug: fromTour?.slug || '' }, skip: !fromTour?.slug, fetchPolicy: 'cache-first',
  });
  const baseTour = baseTourData?.tour || null;
  const baseApplied = useRef(false);

  useEffect(() => {
    if (!baseTour || searchData.countries?.length) return;
    const countries = [];
    (baseTour.itineraryDays || []).forEach((d) => {
      const c = d.primaryPark?.country?.name;
      if (c && !countries.includes(c)) countries.push(c);
    });
    if (!countries.length) return;
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...locationState, searchData: { ...searchData, countries } },
    });
  }, [baseTour]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!baseTour || baseApplied.current || !itinerary.length) return;
    const stays = [];
    (baseTour.itineraryDays || [])
      .slice().sort((a, b) => a.dayNumber - b.dayNumber)
      .forEach((d) => {
        const park = d.primaryPark;
        if (!park || !d.isOvernight) return;
        const last = stays[stays.length - 1];
        if (last && last.parkId === park.id) { last.nights += 1; return; }
        stays.push({
          parkId: park.id, parkName: park.name, parkSlug: park.slug,
          parkCountrySlug: park.country?.slug || '',
          nights: 1, propertyId: d.accommodation?.id || null, migrationZones: [],
        });
      });
    if (!stays.length) return;
    baseApplied.current = true;
    applySIORecommendation({
      stays, category: 'package', categoryLabel: baseTour.name,
      isPriced: false, totalPrice: 0, pricePerPerson: 0, currency: 'USD',
    });
  }, [baseTour, itinerary, applySIORecommendation]);

  /* ─── Clear the applied blueprint, back to the gallery ─── */
  const clearBlueprint = useCallback(() => {
    setSelectedParks({});
    setParkNights({});
    setSelectedSerengetiZones(undefined);
    setAppliedRecommendation(null);
    setSioMode('gallery');
  }, []);

  /* ─── Mutation ─── */
  const [submitCustomTourRequest] = useMutation(SUBMIT_CUSTOM_TOUR_REQUEST, {
    onCompleted: (data) => {
      if (data?.submitCustomTourRequest?.success) setIsSubmitted(true);
      else setSubmitError('Submission failed. Please try again.');
      setIsSubmitting(false);
    },
    onError: (err) => {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    },
  });

  /* ─── Handlers ─── */
  const updateNights = (idx, delta) => {
    const u = [...itinerary];
    const newVal = u[idx].nights + delta;
    const maxForThisLeg = totalNightsFromDates - (u.length - 1);
    if (newVal < 1 || newVal > maxForThisLeg) return;
    u[idx].nights = newVal;
    let remaining = -delta;
    for (let i = u.length - 1; i >= 0 && remaining !== 0; i--) {
      if (i === idx) continue;
      const canChange = remaining > 0
        ? Math.min(remaining, u[i].nights - 1)
        : Math.max(remaining, -(maxForThisLeg - u[i].nights));
      u[i].nights += canChange;
      remaining   -= canChange;
    }
    setItinerary(u);
  };

  const toggleAttraction = (idx, attr) => {
    const u = [...itinerary];
    const found = u[idx].selectedAttractions.find(a => String(a.id) === String(attr.id));
    u[idx].selectedAttractions = found
      ? u[idx].selectedAttractions.filter(a => String(a.id) !== String(attr.id))
      : [...u[idx].selectedAttractions, attr];
    setItinerary(u);
  };

  const commitMomentWithDraft = (country, draft) => {
    if (!draft.name.trim()) return;
    const catObj = experienceCategories.find(c => c.slug === draft.categorySlug);
    const newMoment = {
      ...draft,
      id:       `custom_${country}_${Date.now()}`,
      icon:     CATEGORY_ICONS[draft.categorySlug] || Sparkles,
      category: catObj?.name || draft.categorySlug,
      isCustom: true,
      price:    Number(draft.price) || 0,
    };
    setCustomMoments(prev => ({ ...prev, [country]: [...(prev[country] || []), newMoment] }));
    const legIdx = itinerary.findIndex(l => l.name === country);
    if (legIdx >= 0) {
      const u = [...itinerary];
      u[legIdx].selectedAttractions = [...u[legIdx].selectedAttractions, newMoment];
      setItinerary(u);
    }
  };

  const deleteCustomMoment = (country, momentId) => {
    setCustomMoments(prev => ({
      ...prev, [country]: (prev[country] || []).filter(m => m.id !== momentId),
    }));
    const legIdx = itinerary.findIndex(l => l.name === country);
    if (legIdx >= 0) {
      const u = [...itinerary];
      u[legIdx].selectedAttractions = u[legIdx].selectedAttractions.filter(a => a.id !== momentId);
      setItinerary(u);
    }
  };

  const toggleDietary       = (d) => setDietaryRequirements(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d]);
  const setLegAccommodation = (country, id) => setAccommodation(prev => ({ ...prev, [country]: id }));
  const setCountryMeal      = (country, id) => setPerCountryMeal(prev => ({ ...prev, [country]: id }));

  const goNext = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // No building on dates that have passed — the notice above asks for new ones.
    if (datesNeedFixing) return;
    setStep(s => {
      const next = Math.min(TOTAL_STEPS, s + 1);
      setVisitedSteps(prev => new Set([...prev, next]));
      return next;
    });
  };

  const goPrev = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep(s => {
      const prev = Math.max(1, s - 1);
      // Reset tourist nationalities when returning to Step 1
      // so stale selections from a previous run don't persist
      if (prev === 1) {
        setTourists(t => t.map(tourist => ({ ...tourist, nationality: '' })));
      }
      return prev;
    });
  };

  /* ─── Submitted screen ─── */
  if (isSubmitted) {
    return (
      <div className="f-jost min-h-screen flex flex-col items-center justify-center px-5 dark-sec" style={{ background: NAVY }}>
        <BuilderStyles />
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative z-10 max-w-lg w-full text-center fade-up">
          <div className="pop-in w-20 h-20 flex items-center justify-center mx-auto mb-8"
            style={{ border: `1px solid ${GOLD}`, background: GOLD_DIM }}>
            <CheckCircle2 size={34} strokeWidth={1.5} style={{ color: GOLD }} />
          </div>
          <div className="flex justify-center items-center gap-3 mb-5">
            <div className="w-8 h-px" style={{ background: GOLD }} />
            <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Blueprint Submitted</span>
            <div className="w-8 h-px" style={{ background: GOLD }} />
          </div>
          <h2 className="f-cormorant text-white font-light mb-4" style={{ fontSize: '44px', lineHeight: 1 }}>
            The Blueprint is Ready.
          </h2>
          <p className="f-jost text-slate-400 text-[15px] font-light leading-relaxed mb-10 max-w-sm mx-auto">
            Our travel architects are reviewing your selections for{' '}
            <strong className="text-white font-medium">{user.name}</strong>.
            Expect a detailed proposal within 24 hours.
          </p>
          <div className="grid grid-cols-3 gap-3 mb-10">
            {[
              ['$' + (sidebarTotals.total || 0).toLocaleString(), 'Est. price'],
              [totalNights + ' nights', 'Total Duration'],
              [itinerary.length + ' stops', 'Destinations'],
            ].map(([v, l]) => (
              <div key={l} className="p-4 border text-center" style={{ borderColor: 'rgba(184,151,90,0.2)' }}>
                <p className="f-cormorant text-white font-light text-[29px] leading-none mb-1">{v}</p>
                <p className="f-jost text-[13px] uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.35)' }}>{l}</p>
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/')} className="primary-cta mx-auto">
            <span>Back to safaris</span><ArrowRight size={12} />
          </button>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════
     MAIN RENDER
  ══════════════════════════════════════ */
  return (
    <div className="f-jost min-h-screen overflow-x-hidden" style={{ background: '#F5F3EF' }}>
      <BuilderStyles />

      {/* Nav */}
      <nav className={`builder-nav px-5 sm:px-8 lg:px-10 py-5 flex pt-[118px] justify-between items-center ${isScrolled ? 'scrolled' : ''}`}>
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 f-jost text-[13px] uppercase tracking-[0.16em] transition-colors"
          style={{ color: isScrolled ? '#64748b' : 'rgba(255,255,255,0.6)' }}
          onMouseEnter={e => e.currentTarget.style.color = GOLD}
          onMouseLeave={e => e.currentTarget.style.color = isScrolled ? '#64748b' : 'rgba(255,255,255,0.6)'}>
          <ArrowLeft size={13} strokeWidth={1.5} /> Exit planner
        </button>
        <span className="f-cormorant font-light hidden sm:block" style={{ fontSize: '27px', color: isScrolled ? NAVY : '#fff' }}>
          Safari Planner
        </span>
        <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em]" style={{ color: GOLD }}>
          Phase {step}/{TOTAL_STEPS}
        </span>
      </nav>

      {/* Hero — full-bleed from the very top so the site navbar's fade-to-transparent
          gradient blends into this dark image instead of the page's light background.
          Content clears both fixed nav bars via its own top padding on mobile (where
          it stacks and needs more room) instead of shifting the whole section down. */}
      <div className="relative overflow-hidden flex items-start sm:items-end dark-sec min-h-[520px] sm:min-h-[420px] sm:h-[55vh]"
        style={{ background: NAVY }}>
        <img src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgba(8,14,26,0.65) 0%, rgba(8,14,26,0.4) 40%, rgba(8,14,26,0.9) 100%)' }} />
        <div className="grain absolute inset-0 pointer-events-none opacity-60" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-[180px] sm:pt-0 pb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-7 h-px" style={{ background: GOLD }} />
                <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Tailor-made safari</span>
              </div>
              <h1 className="f-cormorant text-white font-light leading-[0.92] mb-3"
                style={{ fontSize: 'clamp(44px,5.5vw,68px)' }}>
                Plan your<br /><span style={{ color: GOLD }}>own safari.</span>
              </h1>
              <p className="f-jost text-slate-400 text-[15px] font-light max-w-md">
                Customise every detail of your private safari
                {searchData.countries?.length > 0 && (
                  <>{' '}to <span className="text-white">{searchData.countries.join(' · ')}</span></>
                )}.
              </p>
            </div>
            <div className="border p-6 min-w-[220px]"
              style={{ background: 'rgba(245,243,239,0.06)', borderColor: 'rgba(184,151,90,0.25)', backdropFilter: 'blur(12px)' }}>
              <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-2" style={{ color: GOLD }}>Estimated price</p>
              <button
                onClick={() => setShowBreakdownModal(true)}
                className="text-left group"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <p className="f-cormorant text-white font-light leading-none" style={{ fontSize: '44px' }}>
                  {showHeroSkeleton
                    ? <span className="preview-skeleton inline-block w-32" />
                    : `$${(sidebarTotals.total || 0).toLocaleString()}`}
                </p>
                <p className="f-jost text-[12px] uppercase tracking-[0.16em] mt-1" style={{ color: GOLD }}>
                  View breakdown →
                </p>
              </button>
              <p className="f-jost text-[13px] mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {groupSize} {groupSize === 1 ? 'guest' : 'guests'} · {totalNights} nights
                {usingBlueprintQuote
                  ? ` · ${appliedRecommendation.categoryLabel} blueprint`
                  : (summary?.isEstimate && ' · estimate')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="sticky z-40 border-b"
        style={{ top: 0, background: 'rgba(245,243,239,0.95)', backdropFilter: 'blur(16px)', borderColor: '#e2ddd6' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid gap-1 py-4 no-scrollbar overflow-x-auto"
            style={{ gridTemplateColumns: `repeat(${TOTAL_STEPS}, minmax(60px, 1fr))` }}>
            {stepLabels.map((label, i) => (
              <div key={label}
                className="cursor-pointer"
                onClick={() => i + 1 < step && setStep(i + 1)}
                style={{ opacity: i + 1 > step + 1 ? 0.5 : 1 }}>
                <div className={`prog-bar ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : 'pending'}`} />
                <div className="flex items-center gap-2 mt-2">
                  {step > i + 1
                    ? <Check size={10} strokeWidth={2.5} style={{ color: GOLD, flexShrink: 0 }} />
                    : <span className="f-jost text-[12px] font-semibold"
                        style={{ color: step === i + 1 ? GOLD : '#94a3b8' }}>0{i + 1}</span>}
                  <span className="f-jost text-[13px] uppercase tracking-[0.14em] hidden sm:block"
                    style={{ color: step === i + 1 ? NAVY : '#94a3b8' }}>{label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-12 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8 space-y-6">

            {fromTour && (
              <div className="flex flex-wrap items-center justify-between gap-3 border px-5 py-4"
                style={{ borderColor: 'rgba(184,151,90,0.3)', background: '#fff' }}>
                <div>
                  <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.18em]" style={{ color: GOLD }}>Starting from a ready-made safari</p>
                  <p className="f-cormorant text-[22px] text-slate-900">{baseTour?.name || fromTour.name}</p>
                  <p className="f-jost text-[14px] font-light text-slate-500">Its parks, nights and lodges are filled in — change anything you like, then send it to us.</p>
                </div>
                <a href={`/tours/${fromTour.slug}/itinerary`} className="f-jost text-[12.5px] font-semibold uppercase tracking-[0.14em] underline" style={{ color: GOLD }}>
                  View the original
                </a>
              </div>
            )}

            {datesNeedFixing && (
              <div className="border p-5" style={{ borderColor: 'rgba(184,151,90,0.35)', background: 'rgba(184,151,90,0.07)' }}>
                <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.18em] mb-1" style={{ color: GOLD }}>
                  {startISO ? 'Your dates have passed' : 'When are you travelling?'}
                </p>
                <p className="f-jost text-[15px] font-light text-slate-600 mb-4">
                  {startISO
                    ? `The trip was set to start on ${new Date(`${startISO}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. Choose new dates to price it.`
                    : 'Choose your travel dates — every price, park fee and season depends on them.'}
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="f-jost text-[12px] uppercase tracking-[0.14em] text-slate-500">
                    Arrive
                    <input type="date" min={tomorrowISO} max={maxISO} value={newDates.start}
                      onChange={(e) => {
                        const start = e.target.value;
                        setNewDates((d) => {
                          // Starting from a ready-made safari: suggest its own length.
                          const baseNights = fromTour?.nights || baseTour?.durationNights;
                          if (start && baseNights && (!d.end || d.end < start)) {
                            const t = new Date(`${start}T00:00:00`); t.setDate(t.getDate() + baseNights);
                            return { start, end: isoLocal(t) };
                          }
                          return { start, end: d.end && d.end < start ? '' : d.end };
                        });
                      }}
                      className="block mt-1 border border-slate-300 px-3 py-2 text-[15px] normal-case tracking-normal text-slate-900" />
                  </label>
                  <label className="f-jost text-[12px] uppercase tracking-[0.14em] text-slate-500">
                    Depart
                    <input type="date" min={newDates.start || tomorrowISO} max={maxISO} value={newDates.end}
                      onChange={(e) => setNewDates((d) => ({ ...d, end: e.target.value }))}
                      className="block mt-1 border border-slate-300 px-3 py-2 text-[15px] normal-case tracking-normal text-slate-900" />
                  </label>
                  <button type="button" onClick={applyNewDates}
                    disabled={!newDates.start || !newDates.end || newDates.end < newDates.start}
                    className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] px-5 py-3 disabled:opacity-40"
                    style={{ background: GOLD, color: '#0b1220' }}>
                    Use these dates
                  </button>
                </div>
              </div>
            )}

            {/* ══ STEP 1: DURATION & PARTY ══ */}
            {step === 1 && (
              <div className="fade-up space-y-5">
                <StepHeading sub="Step 01 · Duration & Party" title="Dates & Travellers" />

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Party Composition" />
                  <p className="f-jost text-[15px] text-slate-500 font-light mb-5">
                    Add each traveller and their nationality — park fees differ by nationality
                    {searchData.countries?.length > 1 && (
                      <span style={{ color: GOLD }}>{' '}across your {searchData.countries.length} destinations</span>
                    )}
                  </p>
                  <TouristComposer tourists={tourists} onChange={setTourists} maxTourists={20} />
                </div>

                {operatorSeasons.length > 0 && searchData.startDate && (
                  <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                    <SectionLabel text="Season at Your Dates" />
                    <p className="f-jost text-[13px] text-slate-400 font-light mb-4">
                      Final pricing reflects confirmed season rates — shown here as a guide only
                    </p>
                    <div className="space-y-2">
                      {searchData.countries.map(country => {
                        const season = getSeasonHint(country);
                        if (!season) return null;
                        const color = season.color || GOLD;
                        return (
                          <div key={country} className="flex items-center justify-between px-4 py-2.5 border"
                            style={{ borderColor: `${color}40`, background: `${color}0d` }}>
                            <span className="f-jost text-[13px] font-medium text-slate-700">{country}</span>
                            <div className="flex items-center gap-3">
                              <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.14em]" style={{ color }}>{season.name}</span>
                              {season.packageMarkupPercent > 0 && (
                                <span className="f-jost text-[12px]" style={{ color: '#f59e0b' }}>+{season.packageMarkupPercent}% markup</span>
                              )}
                              {season.discountPercent > 0 && (
                                <span className="f-jost text-[12px]" style={{ color: '#4ade80' }}>-{season.discountPercent}% discount</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Nights per Destination" />
                  <p className="f-jost text-[15px] text-slate-500 font-light mb-5">
                    Allocate your {totalNightsFromDates} night{totalNightsFromDates !== 1 ? 's' : ''} across{' '}
                    {itinerary.length} destination{itinerary.length !== 1 ? 's' : ''}.
                    Each stop needs at least 1 night.
                  </p>

                  {searchData.startDate && searchData.endDate && (
                    <div className="flex items-center gap-3 px-5 py-3 mb-6 border"
                      style={{ borderColor: 'rgba(184,151,90,0.25)', background: 'rgba(184,151,90,0.06)' }}>
                      <Calendar size={12} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0 }} />
                      <p className="f-jost text-[13px] font-light" style={{ color: GOLD }}>
                        {new Date(searchData.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {' – '}
                        {new Date(searchData.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        <span style={{ color: 'rgba(184,151,90,0.6)' }}>{' · '}{totalNightsFromDates} nights total</span>
                      </p>
                    </div>
                  )}

                  <div className="space-y-4 stagger">
                    {itinerary.map((leg, idx) => {
                      const maxForThisLeg = totalNightsFromDates - (itinerary.length - 1);
                      const pct = Math.round((leg.nights / totalNightsFromDates) * 100);
                      return (
                        <div key={idx} className="timeline-leg fade-up">
                          <div className="timeline-dot">
                            <span className="f-jost text-[13px] font-semibold" style={{ color: GOLD }}>{idx + 1}</span>
                          </div>
                          <div className="bg-white border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                            style={{ borderColor: '#e8e4de' }}>
                            <div className="flex-1 min-w-0">
                              <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: GOLD }}>Destination {idx + 1}</p>
                              <p className="f-cormorant text-slate-800 font-light text-[27px] leading-none">{leg.name}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <div className="flex-1 h-[2px]" style={{ background: '#f0ece6' }}>
                                  <div className="h-full transition-all duration-300" style={{ width: `${pct}%`, background: GOLD }} />
                                </div>
                                <span className="f-jost text-[13px] flex-shrink-0" style={{ color: '#12100e' }}>{pct}% of trip</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <button className="qty-btn" onClick={() => updateNights(idx, -1)} disabled={leg.nights <= 1}>−</button>
                              <div className="w-20 text-center border" style={{ borderColor: '#e2ddd6', padding: '8px 0' }}>
                                <p className="f-cormorant font-light text-slate-900 text-[32px] leading-none">{leg.nights}</p>
                                <p className="f-jost text-[12px] uppercase tracking-widest text-slate-400">nights</p>
                              </div>
                              <button className="qty-btn" onClick={() => updateNights(idx, 1)} disabled={leg.nights >= maxForThisLeg}>+</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between mt-6 pt-5 border-t" style={{ borderColor: '#f0ece6' }}>
                    <span className="f-jost text-[13px] uppercase tracking-[0.14em] text-slate-500">Total duration</span>
                    <div className="flex items-baseline gap-2">
                      <span className="f-cormorant font-light leading-none text-[32px]"
                        style={{ color: totalNights === totalNightsFromDates ? '#0f172a' : '#f87171' }}>
                        {totalNights}
                      </span>
                      <span className="f-jost text-[13px] text-slate-400">
                        of {totalNightsFromDates} nights · {itinerary.length} stop{itinerary.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => navigate(-1)} className="ghost-cta">
                    <ArrowLeft size={12} strokeWidth={1.5} /> Back
                  </button>
                  <button onClick={goNext} className="primary-cta flex-1">
                    <span>Continue to Experiences</span><ArrowRight size={12} />
                  </button>
                </div>
              </div>
            )}

            {/* ══ STEP 2: EXPERIENCES ══ */}
            {step === 2 && sioMode === 'gallery' && (
              <SIORecommendations
                countrySlugs={searchData.countries.map(c => c.toLowerCase().replace(/\s+/g, '-'))}
                touristComposition={touristCompositionInput}
                startDate={searchData.startDate ? new Date(searchData.startDate).toISOString().split('T')[0] : null}
                endDate={searchData.endDate ? new Date(searchData.endDate).toISOString().split('T')[0] : null}
                ready={summaryReady}
                appliedCategory={appliedRecommendation?.category || null}
                onApply={applySIORecommendation}
                onSkip={() => setSioMode('manual')}
              />
            )}

            {step === 2 && sioMode !== 'gallery' && (
              <div className="fade-up space-y-6">
                {appliedRecommendation && (
                  <SIOAppliedBanner
                    recommendation={appliedRecommendation}
                    onChangeBlueprint={() => setSioMode('gallery')}
                    onClear={clearBlueprint}
                  />
                )}

                <StepParksLodgesExperiences
                  itinerary={itinerary}
                  travelMonth={searchData.startDate ? new Date(searchData.startDate).getMonth() + 1 : null}
                  onNext={goNext}
                  onBack={goPrev}
                  selectedParks={selectedParks}
                  onParksChange={(countryName, parks) =>
                    setSelectedParks(prev => ({ ...prev, [countryName]: parks }))
                  }
                  parkNights={parkNights}
                  onParkNightsChange={(parkId, nights) =>
                    setParkNights(prev => ({ ...prev, [parkId]: nights }))
                  }
                  parkLodges={parkLodges}
                  onParkLodgesChange={(parkId, lodgeIds) =>
                    setParkLodges(prev => ({ ...prev, [parkId]: lodgeIds }))
                  }
                  expDataByIndex={_expData}
                  expLoadingByIndex={_expLoading}
                  customMoments={customMoments}
                  experienceCategories={experienceCategories}
                  onToggleAttraction={toggleAttraction}
                  onCommitMoment={commitMomentWithDraft}
                  onDeleteCustomMoment={deleteCustomMoment}
                  accommodation={accommodation}
                  onAccommodationChange={setLegAccommodation}
                  selectedSerengetiZones={selectedSerengetiZones}
                  onSerengetiZonesChange={setSelectedSerengetiZones}
                />
              </div>
            )}

            {/* ══ STEP 3: MEAL PLAN ══ */}
            {step === 3 && (
              <div className="fade-up space-y-6">
                <StepHeading sub="Step 03 · Meal Plan" title="Curate Your Dining" />
                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Board Basis" />
                  <p className="f-jost text-[15px] text-slate-500 font-light mb-5">
                    Apply one plan to all destinations, or tailor dining to each.
                  </p>
                  <div className="meal-mode-toggle mb-6">
                    <button className={`meal-mode-btn ${mealMode === 'global' ? 'active' : ''}`} onClick={() => setMealMode('global')}>
                      <Globe size={11} strokeWidth={1.5} /> One plan for all
                    </button>
                    <button className={`meal-mode-btn ${mealMode === 'per_country' ? 'active' : ''}`} onClick={() => setMealMode('per_country')}>
                      <MapPin size={11} strokeWidth={1.5} /> Per destination
                    </button>
                  </div>

                  {mealMode === 'global' && (
                    <div className="fade-in flex flex-wrap gap-2">
                      {mealPlans.map(m => {
                        const Icon = m.icon;
                        const isActive = globalMealPlan === m.id;
                        return (
                          <button key={m.id} onClick={() => setGlobalMealPlan(m.id)}
                            className={`toggle-opt ${isActive ? 'active' : ''}`}>
                            <Icon size={12} strokeWidth={1.5} style={{ color: isActive ? GOLD : '#94a3b8' }} />
                            <div className="text-left">
                              <p style={{ fontSize: '13px', letterSpacing: '0.16em' }}>{m.name}</p>
                              <p className="f-jost text-[13px] normal-case tracking-normal text-slate-400 font-light hidden sm:block">{m.desc}</p>
                            </div>
                            {m.price > 0 && (
                              <span className="f-jost text-[13px] ml-2" style={{ color: GOLD }}>+${m.price}/night</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {mealMode === 'per_country' && (
                    <div className="fade-in space-y-2">
                      {itinerary.map(leg => {
                        const accent = getCountryColor(leg.name);
                        const isOpen = expandedMealCountry === leg.name;
                        const selectedPlan = getMealPlanObj(perCountryMeal[leg.name] || globalMealPlan);
                        const SelIcon = selectedPlan.icon;
                        return (
                          <div key={leg.name} className="border overflow-hidden" style={{ borderColor: '#e2ddd6' }}>
                            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                              onClick={() => setExpandedMealCountry(isOpen ? null : leg.name)}>
                              <div className="flex items-center gap-3">
                                <div className="w-2 h-2 rounded-full" style={{ background: accent }} />
                                <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em]" style={{ color: accent }}>{leg.name}</span>
                                <span className="f-jost text-[13px] text-slate-400">{leg.nights} nights</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 border px-3 py-1.5"
                                  style={{ borderColor: `${accent}50`, background: `${accent}0f` }}>
                                  <SelIcon size={11} strokeWidth={1.5} style={{ color: accent }} />
                                  <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.14em]" style={{ color: accent }}>{selectedPlan.name}</span>
                                </div>
                                <ChevronDown size={13} strokeWidth={1.5}
                                  style={{ color: '#12100e', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.25s' }} />
                              </div>
                            </div>
                            {isOpen && (
                              <div className="px-5 pb-5 pt-2 fade-in border-t" style={{ borderColor: '#f0ece6' }}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {mealPlans.map(m => {
                                    const Icon = m.icon;
                                    const isActive = (perCountryMeal[leg.name] || globalMealPlan) === m.id;
                                    return (
                                      <button key={m.id} onClick={() => setCountryMeal(leg.name, m.id)}
                                        className="meal-chip text-left"
                                        style={isActive ? { borderColor: accent, background: `${accent}12` } : {}}>
                                        <div className="w-7 h-7 flex items-center justify-center flex-shrink-0"
                                          style={{ border: `1px solid ${isActive ? accent : '#e2ddd6'}` }}>
                                          <Icon size={12} strokeWidth={1.5} style={{ color: isActive ? accent : '#94a3b8' }} />
                                        </div>
                                        <div className="flex-1 text-left">
                                          <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.14em]" style={{ color: isActive ? accent : NAVY }}>{m.name}</p>
                                          <p className="f-jost text-[13px] font-light text-slate-400">{m.desc}</p>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                          {m.price > 0
                                            ? <span className="f-jost text-[13px] font-semibold" style={{ color: isActive ? accent : GOLD }}>+${m.price}</span>
                                            : <span className="f-jost text-[13px]" style={{ color: '#4ade80' }}>Free</span>}
                                          <p className="f-jost text-[12px] text-slate-400">/night</p>
                                        </div>
                                        {isActive && <Check size={11} strokeWidth={2.5} className="flex-shrink-0" style={{ color: accent }} />}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <button onClick={goPrev} className="ghost-cta">← Back</button>
                  <button onClick={goNext} className="primary-cta flex-1">
                    <span>Continue to Transfers</span><ArrowRight size={12} />
                  </button>
                </div>
              </div>
            )}

            {/* ══ STEP 4: TRANSFERS & PACE ══ */}
            {step === 4 && (
              <div className="fade-up space-y-6">
                <StepHeading sub="Step 04 · Transfers & Pace" title="How Will You Move?" />

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <div className="flex items-center gap-2 mb-5">
                    <SectionLabel text="Ground Transfers" />
                    <Tooltip text="Transfers between airports, hotels and activities">
                      <Info size={12} strokeWidth={1.5} style={{ color: '#12100e' }} className="cursor-pointer mb-3" />
                    </Tooltip>
                  </div>
                  {transfers.length === 0 && <p className="f-jost text-[15px] text-slate-400 font-light py-2">Loading transfer options…</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {transfers.map(t => {
                      const Icon = t.icon;
                      return (
                        <button key={t.id} onClick={() => setTransferType(t.id)}
                          className={`sel-card text-left p-5 ${transferType === t.id ? 'selected' : ''}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="w-8 h-8 flex items-center justify-center" style={{ border: `1px solid ${transferType === t.id ? GOLD : '#e2ddd6'}` }}>
                              <Icon size={13} strokeWidth={1.5} style={{ color: transferType === t.id ? GOLD : '#64748b' }} />
                            </div>
                            {transferType === t.id && <Check size={12} strokeWidth={2.5} style={{ color: GOLD }} />}
                          </div>
                          <h4 className="f-cormorant text-slate-800 font-light text-[22px] leading-none mb-1">{t.name}</h4>
                          <p className="f-jost text-[13px] text-slate-500 font-light">{t.desc}</p>
                          <p className="f-jost text-[13px] mt-3" style={{ color: t.price > 0 ? GOLD : '#4ade80' }}>
                            {t.price > 0 ? `+$${t.price}/night` : 'Included'}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Flight Assistance" />
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <p className="f-cormorant text-slate-800 font-light text-[25px] leading-snug mb-1">Add flight booking support</p>
                      <p className="f-jost text-[15px] text-slate-500 font-light">Our specialists will source and compare international flights. +$150 one-off fee.</p>
                    </div>
                    <button onClick={() => setFlightAssistance(f => !f)} className="flex-shrink-0">
                      <div className="w-12 h-6 relative transition-all duration-300" style={{ background: flightAssistance ? GOLD : '#e2ddd6' }}>
                        <div className="absolute top-1 w-4 h-4 bg-white transition-all duration-300" style={{ left: flightAssistance ? 'calc(100% - 20px)' : '4px' }} />
                      </div>
                    </button>
                  </div>
                </div>

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Travel Pace" />
                  <p className="f-jost text-[15px] text-slate-500 font-light mb-5">This shapes how we structure your days and the density of included activities.</p>
                  {paceOptions.length === 0 && <p className="f-jost text-[15px] text-slate-400 font-light py-2">Loading pace options…</p>}
                  <div className="flex gap-2 flex-wrap">
                    {paceOptions.map(p => {
                      const Icon = p.icon;
                      return (
                        <button key={p.id} onClick={() => setPace(p.id)}
                          className={`pace-opt ${pace === p.id ? 'active' : ''}`} style={{ minWidth: '100px' }}>
                          <Icon size={16} strokeWidth={1.5} style={{ color: pace === p.id ? GOLD : '#94a3b8' }} />
                          <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.14em]" style={{ color: pace === p.id ? NAVY : '#64748b' }}>{p.name}</span>
                          <span className="f-jost text-[13px] text-slate-400 normal-case tracking-normal font-light">{p.sub}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={goPrev} className="ghost-cta">← Back</button>
                  <button onClick={goNext} className="primary-cta flex-1">
                    <span>Continue to Extras</span><ArrowRight size={12} />
                  </button>
                </div>
              </div>
            )}

            {/* ══ STEP 5: EXTRAS ══ */}
            {step === 5 && (
              <div className="fade-up space-y-6">
                <StepHeading sub="Step 05 · Extras & Preferences" title="Personalise Your Journey" />

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <div className="flex items-center gap-2 mb-5">
                    <SectionLabel text="Travel Insurance" />
                    <Tooltip text="We strongly recommend travel insurance for every safari">
                      <AlertCircle size={12} strokeWidth={1.5} style={{ color: GOLD }} className="cursor-pointer mb-3" />
                    </Tooltip>
                  </div>
                  {insuranceOptions.length === 0 && <p className="f-jost text-[15px] text-slate-400 font-light py-2">Loading insurance options…</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {insuranceOptions.map(ins => (
                      <button key={ins.id} onClick={() => setInsurance(ins.id)}
                        className={`ins-card text-left p-5 ${insurance === ins.id ? 'selected' : ''}`}>
                        <div className="flex items-center justify-between mb-3">
                          <Shield size={16} strokeWidth={1.5} style={{ color: insurance === ins.id ? GOLD : '#94a3b8' }} />
                          {insurance === ins.id && <Check size={12} strokeWidth={2.5} style={{ color: GOLD }} />}
                        </div>
                        <h4 className="f-cormorant text-slate-800 font-light text-[22px] leading-none mb-1">{ins.name}</h4>
                        <p className="f-jost text-[13px] text-slate-500 font-light mb-3">{ins.desc}</p>
                        {ins.cover.length > 0 && (
                          <div className="space-y-1 mb-3">
                            {ins.cover.map(c => (
                              <div key={c} className="flex items-center gap-1.5">
                                <Check size={9} strokeWidth={2.5} style={{ color: GOLD }} />
                                <span className="f-jost text-[13px] text-slate-500">{c}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="f-jost text-[13px]" style={{ color: ins.price > 0 ? GOLD : '#4ade80' }}>
                          {ins.price > 0 ? `$${ins.price.toFixed(2)}/person` : 'No charge'}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Dietary Requirements" />
                  <p className="f-jost text-[15px] text-slate-500 font-light mb-5">Select all that apply.</p>
                  <div className="flex flex-wrap gap-2">
                    {(dietaryData?.dietaryOptions || []).map(d => (
                      <button key={d.id} onClick={() => toggleDietary(d.name)}
                        className={`toggle-opt ${dietaryRequirements.includes(d.name) ? 'active' : ''}`}>
                        {dietaryRequirements.includes(d.name) && <Check size={10} strokeWidth={2.5} style={{ color: GOLD }} />}
                        {d.name}
                      </button>
                    ))}
                  </div>
                </div>

                {!user.email && (
                  <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                    <SectionLabel text="Your Details" />
                    <p className="f-jost text-[15px] text-slate-500 font-light mb-5">So our team can send you the tailored plan and price.</p>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Full name"
                        className="bg-transparent border-b f-jost text-[15px] text-slate-800 outline-none font-light pb-3 pt-1" style={{ borderColor: '#ddd9d2' }} />
                      <input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="Email address"
                        className="bg-transparent border-b f-jost text-[15px] text-slate-800 outline-none font-light pb-3 pt-1" style={{ borderColor: '#ddd9d2' }} />
                    </div>
                  </div>
                )}

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Contact Number" />
                  <p className="f-jost text-[15px] text-slate-500 font-light mb-5">Optional — helps our team reach you quickly.</p>
                  <div className="flex items-center gap-3 border-b" style={{ borderColor: '#ddd9d2' }}>
                    <Phone size={13} strokeWidth={1.5} style={{ color: '#12100e', flexShrink: 0 }} />
                    <input type="tel" value={guestPhone} onChange={e => setGuestPhone(e.target.value)}
                      placeholder="e.g. +44 7700 900000"
                      className="flex-1 bg-transparent f-jost text-[15px] text-slate-800 outline-none font-light pb-3 pt-1"
                      style={{ color: '#0f172a' }} />
                  </div>
                </div>

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Accessibility Needs" />
                  <textarea value={accessibilityNeeds} onChange={e => setAccessibilityNeeds(e.target.value)} rows={3}
                    placeholder="e.g. wheelchair accessible lodges required…"
                    className="w-full bg-transparent border-b f-jost text-[15px] text-slate-800 outline-none resize-none font-light"
                    style={{ borderColor: '#ddd9d2', paddingBottom: '12px' }} />
                </div>

                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Special Requests" />
                  <textarea value={specialRequests} onChange={e => setSpecialRequests(e.target.value)} rows={3}
                    placeholder="e.g. celebrating a 30th anniversary, prefer female guides…"
                    className="w-full bg-transparent border-b f-jost text-[15px] text-slate-800 outline-none resize-none font-light"
                    style={{ borderColor: '#ddd9d2', paddingBottom: '12px' }} />
                </div>

                <div className="flex gap-3">
                  <button onClick={goPrev} className="ghost-cta">← Back</button>
                  <button onClick={goNext} className="primary-cta flex-1">
                    <span>Preview Pricing</span><ArrowRight size={12} />
                  </button>
                </div>
              </div>
            )}

            {/* ══ STEP 6: PRICING PREVIEW ══ */}
            {step === 6 && (
              <div className="fade-up space-y-6">
                <StepHeading sub="Step 06 · Pricing Preview" title="Your Price Breakdown" />

                {previewQueryLoading && (
                  <div className="border p-8 flex flex-col items-center gap-4" style={{ background: NAVY, borderColor: 'rgba(184,151,90,0.2)' }}>
                    <Loader2 size={28} strokeWidth={1.5} style={{ color: GOLD, animation: 'spin 1s linear infinite' }} />
                    <p className="f-jost text-[15px] font-light" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      Calculating your full breakdown across all three pricing layers…
                    </p>
                    <p className="f-jost text-[13px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      Government park fees · Supplier room rates · Operator seasons · Experiences
                    </p>
                  </div>
                )}

                {!previewQueryLoading && activePreviewData && (
                  <>
                    <div className="relative overflow-hidden border" style={{ background: NAVY, borderColor: 'rgba(184,151,90,0.2)' }}>
                      <div className="grain absolute inset-0 pointer-events-none opacity-60" />
                      <div style={{ height: '2px', background: `linear-gradient(90deg, ${GOLD}, rgba(184,151,90,0.08))` }} />
                      <div className="relative z-10 p-7">
                        <div className="flex items-start justify-between gap-6 flex-wrap">
                          <div>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-5 h-px" style={{ background: GOLD }} />
                              <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Server-Calculated Price</span>
                            </div>
                            <p className="f-jost text-[15px] font-light" style={{ color: 'rgba(255,255,255,0.4)' }}>
                              {totalNights} nights · {groupSize} {groupSize === 1 ? 'guest' : 'guests'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: 'rgba(184,151,90,0.6)' }}>Total price</p>
                            <p className="f-cormorant text-white font-light leading-none" style={{ fontSize: '48px' }}>
                              ${(activePreviewData.totalPrice || 0).toLocaleString()}
                            </p>
                            <p className="f-jost text-[13px] mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>
                              {activePreviewData.currency || 'USD'} · subject to confirmation
                            </p>
                          </div>
                        </div>

                        {activePreviewData.breakdown?.summary && (
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6 pt-5 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                            {[
                              // agencyServiceNet carries the safari vehicle too — split it out.
                              { label: 'Accommodation', value: `$${Math.max(0, (activePreviewData.breakdown.summary.agencyServiceNet || 0) - (activePreviewData.breakdown.vehicle?.total || 0)).toLocaleString()}` },
                              { label: 'Vehicle',       value: activePreviewData.breakdown.vehicle?.total ? `$${Number(activePreviewData.breakdown.vehicle.total).toLocaleString()}` : 'In final quote' },
                              { label: 'Park Fees',     value: `$${(activePreviewData.breakdown.summary.statutoryParkFees || 0).toLocaleString()}` },
                              { label: 'Experiences',  value: `$${(activePreviewData.breakdown.summary.experiencesTotal || 0).toLocaleString()}` },
                              { label: 'Tax',          value: `$${(activePreviewData.breakdown.summary.taxAmount || 0).toLocaleString()}` },
                            ].map(({ label, value }) => (
                              <div key={label}>
                                <p className="f-jost text-[12px] uppercase tracking-[0.16em] mb-1" style={{ color: 'rgba(255,255,255,0.3)' }}>{label}</p>
                                <p className="f-cormorant text-white font-light leading-none" style={{ fontSize: '27px' }}>{value}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border overflow-hidden" style={{ borderColor: '#e2ddd6' }}>
                      <div style={{ height: '2px', background: `linear-gradient(90deg, ${GOLD}, rgba(184,151,90,0.08))` }} />
                      <div className="bg-white p-7">
                        <SectionLabel text="Full Breakdown" />
                        <p className="f-jost text-[13px] text-slate-400 font-light mb-6">
                          Three-layer pricing — government park fees (incl. VAT) · supplier room rates · operator season adjustments
                        </p>
                        <PricingBreakdown
                          lines={serverLines}
                          totals={{
                            tax:       activePreviewData.tax,
                            taxLabel:  activePreviewData.taxLabel || 'VAT',
                            total:     activePreviewData.totalPrice,
                            perPerson: activePreviewData.pricePerPerson,
                            currency:  activePreviewData.currency || 'USD',
                          }}
                          theme="light"
                          showPerPerson={true}
                        />
                      </div>
                    </div>
                  </>
                )}

                {!previewQueryLoading && !activePreviewData && (
                  <div className="border p-7" style={{ borderColor: '#e2ddd6', background: '#fff' }}>
                    <p className="f-jost text-[15px] text-slate-500 font-light">
                      Preview could not be generated. Please ensure accommodation is selected and try again.
                    </p>
                    <button onClick={() => {
                      const selectedExperienceIds = itinerary.flatMap(leg =>
                        leg.selectedAttractions.filter(a => !a.isCustom && a.dbId).map(a => String(a.dbId))
                      );
                      fetchPreview({
                        variables: {
                          countrySlugs:          searchData.countries.map(c => c.toLowerCase().replace(/\s+/g, '-')),
                          touristComposition:    touristCompositionInput,
                          startDate:             searchData.startDate ? new Date(searchData.startDate).toISOString().split('T')[0] : null,
                          endDate:               searchData.endDate   ? new Date(searchData.endDate).toISOString().split('T')[0]   : null,
                          selectedPropertyIds,
                          selectedExperienceIds: selectedExperienceIds.length > 0 ? selectedExperienceIds : undefined,
                          selectedParkIds:       derivedSelectedParkIds,
                          parkNightsJson,
                          migrationZonesJson,
                          channel:               'b2c',
                        },
                      });
                    }} className="f-jost text-[13px] uppercase tracking-[0.14em] mt-3" style={{ color: GOLD }}>
                      Retry →
                    </button>
                  </div>
                )}

                <div className="flex gap-3">
                  <button onClick={goPrev} className="ghost-cta">← Back</button>
                  <button onClick={goNext} className="primary-cta flex-1">
                    <span>Continue to Review</span><ArrowRight size={12} />
                  </button>
                </div>
              </div>
            )}

            {/* ══ STEP 7: REVIEW & SUBMIT ══ */}
            {step === 7 && (
              <div className="fade-up space-y-6">
                <StepHeading sub="Step 07 · Review" title="Your Final Blueprint" />

                <div className="relative overflow-hidden" style={{ background: NAVY, border: '1px solid rgba(184,151,90,0.2)' }}>
                  <div className="absolute inset-0 grain pointer-events-none opacity-60" />
                  <div style={{ height: '2px', background: `linear-gradient(90deg, ${GOLD}, rgba(184,151,90,0.08))` }} />
                  <div className="relative z-10 p-7">
                    <div className="flex items-start justify-between gap-6 flex-wrap">
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-5 h-px" style={{ background: GOLD }} />
                          <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Your safari plan</span>
                        </div>
                        <h3 className="f-cormorant text-white font-light leading-none mb-2" style={{ fontSize: '34px' }}>
                          {itinerary.map(l => l.name).join(' → ')}
                        </h3>
                        <p className="f-jost text-[15px] font-light" style={{ color: 'rgba(255,255,255,0.4)' }}>
                          {totalNights} nights · {groupSize} {groupSize === 1 ? 'guest' : 'guests'} · {totalAttractions} experiences
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: 'rgba(184,151,90,0.6)' }}>Estimated price</p>
                        <p className="f-cormorant text-white font-light leading-none" style={{ fontSize: '48px' }}>
                          ${(sidebarTotals.total || 0).toLocaleString()}
                        </p>
                        <p className="f-jost text-[13px] mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>
                          {sidebarTotals.currency || 'USD'} · subject to confirmation
                        </p>
                      </div>
                    </div>
                    <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '20px 0' }} />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {[
                        { v: itinerary.length, l: 'Destinations' },
                        { v: totalNights,      l: 'Total Nights'  },
                        { v: groupSize,        l: groupSize === 1 ? 'Traveller' : 'Travellers' },
                        { v: totalAttractions, l: 'Experiences'  },
                      ].map(({ v, l }) => (
                        <div key={l}>
                          <p className="f-cormorant text-white font-light leading-none mb-1" style={{ fontSize: '32px' }}>{v}</p>
                          <p className="f-jost text-[12px] uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.3)' }}>{l}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Itinerary accordion */}
                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Itinerary · Destination by Destination" />
                  <p className="f-jost text-[15px] text-slate-400 font-light mb-6">Click any card to expand. Use edit links to jump back.</p>
                  <div className="space-y-4">
                    {itinerary.map((leg, idx) => {
                      const legProps = getCountryProperties(leg.name);
                      const selAcc   = legProps.find(o => o.id === accommodation[leg.name]);
                      const accent   = getCountryColor(leg.name);
                      const meal     = getMealPlanObj(getMealForCountry(leg.name));
                      const MealIcon = meal.icon;
                      const isOpen   = !!expandedReview[idx];
                      return (
                        <div key={idx} className="timeline-leg pb-5">
                          <div className="timeline-dot">
                            <span className="f-cormorant text-white font-light"
                              style={{ fontSize: '15px', background: GOLD, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {idx + 1}
                            </span>
                          </div>
                          <div className="border overflow-hidden"
                            style={{ borderColor: isOpen ? accent : '#e8e4de', transition: 'border-color 0.3s' }}>
                            <div style={{ height: '3px', background: `linear-gradient(90deg, ${accent}, rgba(0,0,0,0))` }} />
                            <button className="w-full text-left p-5"
                              style={{ background: isOpen ? `${accent}06` : '#fff', transition: 'background 0.25s' }}
                              onClick={() => setExpandedReview(p => ({ ...p, [idx]: !p[idx] }))}>
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <span className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] block mb-1" style={{ color: accent }}>
                                    Stop {idx + 1} · {leg.nights} {leg.nights === 1 ? 'night' : 'nights'}
                                  </span>
                                  <h3 className="f-cormorant text-slate-900 font-light leading-none" style={{ fontSize: '30px' }}>{leg.name}</h3>
                                  {!isOpen && selAcc && (
                                    <p className="f-jost text-[13px] text-slate-400 mt-2 flex items-center gap-2">
                                      <Hotel size={9} strokeWidth={1.5} style={{ color: accent }} />
                                      {selAcc.name}
                                    </p>
                                  )}
                                </div>
                                <ChevronDown size={14} strokeWidth={1.5}
                                  style={{ color: isOpen ? accent : '#cbd5e1', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s', marginTop: '4px', flexShrink: 0 }} />
                              </div>
                            </button>
                            <div className={`review-expand ${isOpen ? 'open' : 'closed'}`}>
                              <div style={{ borderTop: `1px solid ${accent}25` }}>
                                <div className="p-5 pt-4">
                                  {/* Edit links */}
                                  <div className="flex items-center gap-1 mb-4 pb-3 border-b flex-wrap" style={{ borderColor: '#f0ece6' }}>
                                    <span className="f-jost text-[12px] uppercase tracking-[0.16em] text-slate-400 mr-2">Edit:</span>
                                    {[
                                      { label: 'Duration',    s: 1 },
                                      { label: 'Experiences', s: 2 },
                                      { label: 'Meal Plan',   s: 3 },
                                      { label: 'Transfers',   s: 4 },
                                    ].map(({ label, s }) => (
                                      <button key={label}
                                        onClick={() => { setStep(s); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                        className="f-jost text-[12px] uppercase tracking-[0.14em] px-2 py-1 transition-all"
                                        style={{ color: '#12100e', border: '1px solid transparent' }}
                                        onMouseEnter={e => { e.currentTarget.style.color = accent; e.currentTarget.style.borderColor = `${accent}40`; e.currentTarget.style.background = `${accent}08`; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.background = 'transparent'; }}>
                                        {label} →
                                      </button>
                                    ))}
                                  </div>

                                  {/* Lodge + Meal */}
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                                    <div className="flex items-start gap-3 p-3 border"
                                      style={{ borderColor: `${accent}30`, background: `${accent}08` }}>
                                      <Hotel size={12} strokeWidth={1.5} style={{ color: accent, flexShrink: 0, marginTop: 2 }} />
                                      <div>
                                        <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.16em] mb-0.5" style={{ color: accent }}>Lodge</p>
                                        <p className="f-cormorant text-slate-800 font-light leading-snug" style={{ fontSize: '18px' }}>{selAcc?.name || 'Not configured'}</p>
                                        {selAcc && <p className="f-jost text-[13px] text-slate-400">{selAcc.tier} · ${selAcc.price}/night</p>}
                                        {selAcc && leg.nights && (
                                          <p className="f-jost text-[13px] mt-1" style={{ color: accent }}>
                                            {leg.nights} nights · est. ${(selAcc.price * leg.nights).toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-3 border" style={{ borderColor: '#e8e4de', background: '#fafaf8' }}>
                                      <MealIcon size={12} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0, marginTop: 2 }} />
                                      <div>
                                        <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.16em] mb-0.5" style={{ color: GOLD }}>Meal Plan</p>
                                        <p className="f-cormorant text-slate-800 font-light leading-snug" style={{ fontSize: '18px' }}>{meal.name}</p>
                                        <p className="f-jost text-[13px] text-slate-400">{meal.desc}</p>
                                        {meal.price > 0 && (
                                          <p className="f-jost text-[13px] mt-1" style={{ color: GOLD }}>+${meal.price}/pp/night</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Parks */}
                                  {(selectedParks[leg.name] || []).length > 0 && (
                                    <div className="mb-4 p-3 border" style={{ borderColor: '#e8e4de', background: '#fafaf8' }}>
                                      <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.16em] mb-2" style={{ color: '#12100e' }}>
                                        Parks · {(selectedParks[leg.name] || []).length} selected
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        {(selectedParks[leg.name] || []).map(park => {
                                          const nights = parkNights[park.id] || leg.nights;
                                          return (
                                            <div key={park.id} className="flex items-center gap-2 px-3 py-1.5"
                                              style={{ border: `1px solid ${accent}30`, background: `${accent}08` }}>
                                              <MapPin size={9} strokeWidth={1.5} style={{ color: accent }} />
                                              <span className="f-jost text-[13px]" style={{ color: accent }}>{park.name}</span>
                                              <span className="f-jost text-[12px] text-slate-400">· {nights}n</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {/* Migration zones */}
                                  {Array.isArray(selectedSerengetiZones) && selectedSerengetiZones.length > 0 &&
                                    (selectedParks[leg.name] || []).some(p => (p.slug || '').includes('serengeti') || (p.name || '').toLowerCase().includes('serengeti')) && (
                                    <div className="mb-4 p-3 border" style={{ borderColor: 'rgba(245,158,11,0.25)', background: 'rgba(245,158,11,0.04)' }}>
                                      <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.16em] mb-2" style={{ color: '#f59e0b' }}>
                                        Migration Zones
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        {selectedSerengetiZones.map(z => (
                                          <span key={z.key} className="f-jost text-[12px] font-semibold uppercase tracking-[0.14em] px-2 py-1"
                                            style={{ background: `${z.color}18`, border: `1px solid ${z.color}40`, color: z.color }}>
                                            {z.label}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Transfer */}
                                  {transferType && (
                                    <div className="mb-4 p-3 border flex items-center gap-3" style={{ borderColor: '#e8e4de', background: '#fafaf8' }}>
                                      <Car size={12} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0 }} />
                                      <div>
                                        <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.16em] mb-0.5" style={{ color: GOLD }}>Transfer</p>
                                        <p className="f-cormorant text-slate-800 font-light" style={{ fontSize: '17px' }}>
                                          {transfers.find(t => t.id === transferType)?.name || transferType}
                                        </p>
                                        {transfers.find(t => t.id === transferType)?.price > 0 && (
                                          <p className="f-jost text-[13px] text-slate-400">
                                            +${transfers.find(t => t.id === transferType)?.price}/night
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Park fees from breakdown */}
                                  {activePreviewData?.breakdown?.parkFees && (() => {
                                    const legParkNames = (selectedParks[leg.name] || []).map(p => p.name);
                                    const legDays = activePreviewData.breakdown.parkFees.filter(d =>
                                      legParkNames.some(n => d.park?.toLowerCase().includes(n.toLowerCase()) ||
                                        n.toLowerCase().includes((d.park || '').toLowerCase()))
                                    );
                                    if (!legDays.length) return null;
                                    const legFeeTotal = legDays.reduce((s, d) => s + (d.dayTotalGross || 0), 0);
                                    return (
                                      <div className="mb-4 p-3 border" style={{ borderColor: '#e8e4de', background: '#fafaf8' }}>
                                        <div className="flex items-center justify-between mb-2">
                                          <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.16em]" style={{ color: '#12100e' }}>
                                            Park & Conservation Fees
                                          </p>
                                          <span className="f-cormorant font-light text-slate-800" style={{ fontSize: '18px' }}>
                                            ${legFeeTotal.toLocaleString()}
                                          </span>
                                        </div>
                                        {[...new Set(legDays.map(d => d.park))].map(parkName => {
                                          const parkDays  = legDays.filter(d => d.park === parkName);
                                          const parkTotal = parkDays.reduce((s, d) => s + (d.dayTotalGross || 0), 0);
                                          const zone      = parkDays[0]?.migrationZone;
                                          return (
                                            <div key={parkName} className="flex items-center justify-between">
                                              <span className="f-jost text-[13px] text-slate-500 flex items-center gap-1">
                                                <MapPin size={8} strokeWidth={1.5} style={{ color: accent }} />
                                                {parkName}
                                                {zone && <span style={{ color: GOLD }}> · {zone}</span>}
                                                <span className="text-slate-400"> · {parkDays.length}d</span>
                                              </span>
                                              <span className="f-jost text-[13px] text-slate-600">${parkTotal.toLocaleString()}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}

                                  {/* Experiences */}
                                  {leg.selectedAttractions.length > 0 ? (
                                    <div>
                                      <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-2" style={{ color: '#12100e' }}>
                                        Experiences · {leg.selectedAttractions.length} selected
                                      </p>
                                      <div className="space-y-1">
                                        {leg.selectedAttractions.map(a => {
                                          const expLine = activePreviewData?.breakdown?.experiences?.items
                                            ?.find(e => e.experienceId === a.dbId || e.name === a.name);
                                          return (
                                            <div key={a.id} className="flex items-center justify-between px-3 py-1.5"
                                              style={{ border: `1px solid ${GOLD_DIM}`, background: GOLD_DIM }}>
                                              <div className="flex items-center gap-2">
                                                {a.isCustom && <Sparkles size={8} strokeWidth={2} style={{ color: GOLD }} />}
                                                <span className="f-jost text-[13px]" style={{ color: '#12100e' }}>{a.name}</span>
                                                {a.duration && <span className="f-jost text-[12px] text-slate-400">· {a.duration}</span>}
                                              </div>
                                              <span className="f-jost text-[13px]" style={{ color: GOLD }}>
                                                {expLine ? `$${expLine.lineTotal.toLocaleString()}` : a.price > 0 ? `$${(a.price * groupSize).toLocaleString()}` : 'On request'}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 p-3" style={{ background: '#fafaf8', border: '1px solid #f0ece6' }}>
                                      <Info size={11} strokeWidth={1.5} style={{ color: '#6b7280' }} />
                                      <p className="f-jost text-[13px] text-slate-400">No experiences selected</p>
                                      <button onClick={() => { setStep(2); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                        className="f-jost text-[13px] uppercase tracking-[0.14em] ml-auto" style={{ color: GOLD }}>Add →</button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Full pricing breakdown */}
                {activePreviewData ? (
                  <div className="border overflow-hidden" style={{ borderColor: '#e2ddd6' }}>
                    <div style={{ height: '2px', background: `linear-gradient(90deg, ${GOLD}, rgba(184,151,90,0.08))` }} />
                    <div className="bg-white p-7">
                      <SectionLabel text="Full Pricing Breakdown" />
                      <p className="f-jost text-[13px] text-slate-400 font-light mb-6">
                        Accommodation · park fees · meal plan · transfers · experiences · insurance · VAT
                      </p>
                      <PricingBreakdown
                        lines={serverLines}
                        totals={{
                          tax:       activePreviewData.tax,
                          taxLabel:  activePreviewData.taxLabel || 'VAT',
                          total:     activePreviewData.totalPrice,
                          perPerson: activePreviewData.pricePerPerson,
                          currency:  activePreviewData.currency || 'USD',
                        }}
                        theme="light"
                        showPerPerson={true}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="border p-5 flex items-center justify-between" style={{ borderColor: '#e2ddd6', background: '#fafaf8' }}>
                    <p className="f-jost text-[15px] text-slate-400 font-light">
                      Pricing breakdown not yet generated.
                    </p>
                    <button
                      onClick={() => { setStep(6); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="f-jost text-[13px] uppercase tracking-[0.14em]"
                      style={{ color: GOLD }}>
                      Generate in Step 6 →
                    </button>
                  </div>
                )}

                {/* Trip settings */}
                <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                  <SectionLabel text="Trip Settings" />
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { label: 'Meal Plan',     value: getMealPlanObj(globalMealPlan).name, sub: mealMode === 'global' ? 'All destinations' : 'Custom per destination', icon: Utensils, edit: 3 },
                      { label: 'Transfers',     value: transfers.find(t => t.id === transferType)?.name,    sub: transfers.find(t => t.id === transferType)?.desc,    icon: Car,      edit: 4 },
                      { label: 'Travel Pace',   value: paceOptions.find(p => p.id === pace)?.name,         sub: paceOptions.find(p => p.id === pace)?.sub,           icon: Zap,      edit: 4 },
                      { label: 'Insurance',     value: insurance ? insuranceOptions.find(i => i.id === insurance)?.name : 'None selected', sub: insurance ? '' : 'Add in step 5', icon: Shield, edit: 5 },
                      { label: 'Group Size',    value: `${groupSize} ${groupSize === 1 ? 'traveller' : 'travellers'}`, sub: 'Private safari', icon: Users, edit: 1 },
                      { label: 'Flight Assist', value: flightAssistance ? 'Included' : 'Not added', sub: flightAssistance ? '+$150 · one-off' : 'Add in step 5', icon: Plane, edit: 4 },
                    ].map(({ label, value, sub, icon: Icon, edit }) => (
                      <button key={label}
                        onClick={() => { setStep(edit); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="text-left p-4 border transition-all"
                        style={{ borderColor: '#e2ddd6', background: '#fafaf8' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = GOLD_DIM; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2ddd6'; e.currentTarget.style.background = '#fafaf8'; }}>
                        <div className="flex items-center justify-between mb-2">
                          <Icon size={12} strokeWidth={1.5} style={{ color: GOLD }} />
                          <span className="f-jost text-[12px] uppercase tracking-[0.14em]" style={{ color: '#12100e' }}>edit</span>
                        </div>
                        <p className="f-jost text-[12px] uppercase tracking-[0.16em] mb-1" style={{ color: GOLD }}>{label}</p>
                        <p className="f-cormorant text-slate-800 font-light leading-snug" style={{ fontSize: '18px' }}>{value}</p>
                        {sub && <p className="f-jost text-[13px] text-slate-400 font-light mt-0.5">{sub}</p>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Serengeti zones in review */}
                {Array.isArray(selectedSerengetiZones) && selectedSerengetiZones.length > 0 && (
                  <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                    <SectionLabel text="Serengeti Migration Zones" />
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedSerengetiZones.map(z => (
                        <span key={z.key} className="f-jost text-[12px] font-semibold uppercase tracking-[0.16em] px-3 py-1.5"
                          style={{ background: `${z.color}18`, border: `1px solid ${z.color}40`, color: z.color }}>
                          {z.label}
                        </span>
                      ))}
                    </div>
                    <button onClick={() => { setStep(2); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="f-jost text-[13px] uppercase tracking-[0.14em] mt-3 block" style={{ color: GOLD }}>
                      Edit zones →
                    </button>
                  </div>
                )}

                {/* Preferences */}
                {(specialRequests || accessibilityNeeds || dietaryRequirements.length > 0 || guestPhone) && (
                  <div className="bg-white border p-7" style={{ borderColor: '#e2ddd6' }}>
                    <SectionLabel text="Preferences & Requests" />
                    <div className="space-y-5 mt-3">
                      {guestPhone && (
                        <div>
                          <p className="f-jost text-[12px] uppercase tracking-[0.16em] mb-1" style={{ color: '#12100e' }}>Contact Number</p>
                          <p className="f-jost text-[15px] text-slate-600 font-light">{guestPhone}</p>
                        </div>
                      )}
                      {dietaryRequirements.length > 0 && (
                        <div>
                          <p className="f-jost text-[12px] uppercase tracking-[0.16em] mb-2" style={{ color: '#12100e' }}>Dietary Requirements</p>
                          <div className="flex flex-wrap gap-2">
                            {dietaryRequirements.map(d => <span key={d} className="tag-badge">{d}</span>)}
                          </div>
                        </div>
                      )}
                      {accessibilityNeeds && (
                        <div>
                          <p className="f-jost text-[12px] uppercase tracking-[0.16em] mb-1" style={{ color: '#12100e' }}>Accessibility</p>
                          <p className="f-jost text-[15px] text-slate-600 font-light leading-relaxed">{accessibilityNeeds}</p>
                        </div>
                      )}
                      {specialRequests && (
                        <div>
                          <p className="f-jost text-[12px] uppercase tracking-[0.16em] mb-1" style={{ color: '#12100e' }}>Special Requests</p>
                          <div className="flex items-start gap-3 p-3" style={{ background: GOLD_DIM, border: '1px solid rgba(184,151,90,0.2)' }}>
                            <div className="w-px self-stretch flex-shrink-0" style={{ background: GOLD }} />
                            <p className="f-cormorant text-slate-700 font-light leading-relaxed" style={{ fontSize: '18px' }}>"{specialRequests}"</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Trust panel */}
                <div className="relative overflow-hidden border grain" style={{ background: NAVY, borderColor: 'rgba(184,151,90,0.2)' }}>
                  <div style={{ height: '2px', background: `linear-gradient(90deg, ${GOLD}, rgba(184,151,90,0.08))` }} />
                  <div className="p-7">
                    <div className="grid grid-cols-3 gap-4 text-center mb-6">
                      {[
                        { icon: Shield, label: 'Secure Proposal', sub: 'No payment now' },
                        { icon: Clock,  label: '24h Response',    sub: 'Expert review'  },
                        { icon: Heart,  label: 'No Obligation',   sub: 'Change anytime' },
                      ].map(({ icon: Icon, label, sub }) => (
                        <div key={label}>
                          <div className="w-9 h-9 flex items-center justify-center mx-auto mb-2"
                            style={{ border: '1px solid rgba(184,151,90,0.25)', background: 'rgba(184,151,90,0.06)' }}>
                            <Icon size={14} strokeWidth={1.5} style={{ color: GOLD }} />
                          </div>
                          <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.14em] text-white mb-0.5">{label}</p>
                          <p className="f-jost text-[13px] font-light" style={{ color: 'rgba(255,255,255,0.35)' }}>{sub}</p>
                        </div>
                      ))}
                    </div>
                    <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', marginBottom: '20px' }} />
                    <p className="f-cormorant text-center font-light mb-1" style={{ fontSize: '27px', color: 'rgba(255,255,255,0.7)' }}>
                      Ready to send us your safari?
                    </p>
                    <p className="f-jost text-center text-[15px] font-light" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      Our travel architects will review your blueprint and send a full proposal within 24 hours.
                    </p>
                  </div>
                </div>

                {submitError && (
                  <div className="p-4 border" style={{ borderColor: '#f87171', background: 'rgba(248,113,113,0.05)' }}>
                    <p className="f-jost text-[15px] text-red-500">{submitError}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button onClick={goPrev} className="ghost-cta">← Back</button>
                  <button
                    className="primary-cta flex-1"
                    style={{ opacity: isSubmitting ? 0.6 : 1 }}
                    onClick={() => {
                      if (isSubmitting) return;
                      setSubmitError(null);
                      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((guestEmail || '').trim())) {
                        setSubmitError('Please add your email address (Extras step) so we can send you the plan.');
                        return;
                      }
                      setIsSubmitting(true);
                      submitCustomTourRequest({
                        variables: {
                          input: {
                            guestName:  guestName.trim() || 'Explorer',
                            guestEmail: guestEmail.trim(),
                            guestPhone: guestPhone  || user?.phone || '',
                            selectedCountrySlugs: searchData.countries.map(
                              c => c.toLowerCase().replace(/\s+/g, '-')
                            ),
                            selectedParkIds:       derivedSelectedParkIds,
                            selectedExperienceIds: itinerary.flatMap(leg =>
                              leg.selectedAttractions.filter(a => !a.isCustom && a.dbId).map(a => String(a.dbId))
                            ),
                            startDate: searchData.startDate
                              ? new Date(searchData.startDate).toISOString().split('T')[0] : null,
                            endDate: searchData.endDate
                              ? new Date(searchData.endDate).toISOString().split('T')[0] : null,
                            touristComposition:     touristCompositionInput,
                            accommodationLevelSlug: derivedAccommodationLevelSlug,
                            insuranceId: insuranceOptions.find(i => i.id === insurance)?.dbId || undefined,
                            // ── Previously dropped — the fields that define the actual trip ──
                            selectedPropertyIds:  selectedPropertyIds.length > 0 ? selectedPropertyIds : undefined,
                            parkNightsJson,        // memo already returns a JSON string or undefined
                            migrationZonesJson,    // memo already returns a JSON string or undefined
                            transferTypeId:       transfers.find(t => t.id === transferType)?.dbId || undefined,
                            travelPaceId:         paceOptions.find(p => p.id === pace)?.dbId || undefined,
                            specialRequirements:  specialRequests || undefined,
                            itinerarySource:      fromTour ? 'package' : appliedRecommendation ? 'ga_suggestion' : 'user',
                            basedOnTourId:        baseTour?.id || undefined,
                            sioRecommendation:    appliedRecommendation && !fromTour ? JSON.stringify({
                              category:             appliedRecommendation.category,
                              categoryLabel:        appliedRecommendation.categoryLabel,
                              title:                appliedRecommendation.title,
                              quotedTotal:          appliedRecommendation.totalPrice,
                              quotedPricePerPerson: appliedRecommendation.pricePerPerson,
                              reasons:              appliedRecommendation.reasons,
                            }) : undefined,
                            
                            calculatedTotal: activePreviewData?.totalPrice || sidebarTotals.total || null,
                            priceBreakdown: JSON.stringify({
                              serverCalculated: !!activePreviewData,
                              totalPrice:       activePreviewData?.totalPrice     || sidebarTotals.total,
                              pricePerPerson:   activePreviewData?.pricePerPerson || sidebarTotals.perPerson,
                              breakdown:        activePreviewData?.breakdown      || null,
                              selectedPropertyIds,
                              selectedParkIds:  derivedSelectedParkIds,
                              parkNights:       parkNightsJson ? JSON.parse(parkNightsJson) : {},
                              selectedSerengetiZones: Array.isArray(selectedSerengetiZones)
                                ? selectedSerengetiZones.map(z => ({ key: z.key, label: z.label }))
                                : null,
                              locationName,
                              mealMode, globalMealPlan, perCountryMeal,
                              transferType, pace, flightAssistance,
                              insurance:           insurance || null,
                              dietaryRequirements, accessibilityNeeds, specialRequests,
                              itinerarySource: appliedRecommendation
                                ? `sio:${appliedRecommendation.category}`
                                : 'manual',
                              sioRecommendation: appliedRecommendation ? {
                                category:             appliedRecommendation.category,
                                categoryLabel:        appliedRecommendation.categoryLabel,
                                title:                appliedRecommendation.title,
                                quotedTotal:          appliedRecommendation.totalPrice,
                                quotedPricePerPerson: appliedRecommendation.pricePerPerson,
                                reasons:              appliedRecommendation.reasons,
                              } : null,
                              customMoments: Object.fromEntries(
                                Object.entries(customMoments).map(([country, moments]) => [
                                  country,
                                  moments.map(m => ({
                                    name: m.name, category: m.category,
                                    difficulty: m.difficulty, duration: m.duration,
                                    price: m.price, desc: m.desc,
                                  })),
                                ])
                              ),
                            }),
                          },
                        },
                      });
                    }}
                  >
                    <span>{isSubmitting ? 'Submitting…' : 'Commission Blueprint'}</span>
                    <CreditCard size={13} />
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* ── SIDEBAR ── */}
          <div className="lg:col-span-4" ref={sidebarRef}>
            <div className="sticky top-[120px] space-y-4">
              <div className="border" style={{ background: NAVY, borderColor: 'rgba(184,151,90,0.2)' }}>
                <div className="h-[2px]" style={{ background: `linear-gradient(90deg, ${GOLD}, rgba(184,151,90,0.1))` }} />
                <div className="p-7">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-5 h-px" style={{ background: GOLD }} />
                    <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Trip summary</span>
                  </div>

                  <div className="flex items-center gap-3 pb-5 mb-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                    <div className="w-8 h-8 flex items-center justify-center" style={{ border: '1px solid rgba(184,151,90,0.3)' }}>
                      <span className="f-cormorant text-white text-[18px]">{user?.name?.[0] || 'E'}</span>
                    </div>
                    <div>
                      <p className="f-jost text-[13px] uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.4)' }}>Traveller</p>
                      <p className="f-jost text-[15px] text-white font-light">{user?.name || 'Explorer'}</p>
                    </div>
                  </div>

                  {showSidebarSkeleton ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="preview-skeleton" style={{ width: `${60 + i * 10}%` }} />
                      ))}
                    </div>
                  ) : (
                    <PricingBreakdown
                      lines={sidebarLines}
                      totals={sidebarTotals}
                      theme="dark"
                      showPerPerson={true}
                    />
                  )}

                  {/* Serengeti zone chips */}
                  {Array.isArray(selectedSerengetiZones) && selectedSerengetiZones.length > 0 && (
                    <div className="mt-3 pt-3 border-t space-y-1" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                      {selectedSerengetiZones.map(zone => (
                        <div key={zone.key} className="flex items-center gap-2">
                          <MapPin size={9} strokeWidth={1.5} style={{ color: zone.color || GOLD }} />
                          <span className="f-jost text-[12px]" style={{ color: zone.color || GOLD }}>{zone.label}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* SIO blueprint badge */}
                  {appliedRecommendation && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                      <Sparkles size={9} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0 }} />
                      <span className="f-jost text-[12px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        Blueprint · {appliedRecommendation.categoryLabel}
                      </span>
                    </div>
                  )}

                  {/* Edited-since-blueprint hint */}
                  {appliedRecommendation && editedSinceBlueprint && step < 6 && (
                    <p className="f-jost text-[12px] mt-3 pt-3 border-t"
                      style={{ color: 'rgba(245,158,11,0.7)', borderColor: 'rgba(255,255,255,0.07)' }}>
                      ✦ Edited since blueprint — final price confirmed at Step 6
                    </p>
                  )}

                  {/* Blueprint-price hint */}
                  {usingBlueprintQuote && !editedSinceBlueprint && (
                    <p className="f-jost text-[12px] mt-3 pt-3 border-t"
                      style={{ color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.07)' }}>
                      ✦ Blueprint price · server-calculated · confirmed at Step 6
                    </p>
                  )}

                  {/* Estimate hint (pure manual path only) */}
                  {!usingBlueprintQuote && summary?.isEstimate && step < 6 && (
                    <p className="f-jost text-[12px] mt-3 pt-3 border-t"
                      style={{ color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.07)' }}>
                      ✦ Estimate based on lowest available rate · Confirmed in Step 6
                    </p>
                  )}
                </div>
              </div>

              {/* Stats strip */}
              <div className="grid grid-cols-3 gap-px bg-[#e2ddd6]">
                {[
                  [totalNights + '', 'nights'],
                  [groupSize + '', 'guests'],
                  [totalAttractions + '', 'experiences'],
                ].map(([v, l]) => (
                  <div key={l} className="bg-white p-4 text-center">
                    <p className="f-cormorant text-slate-900 font-light leading-none" style={{ fontSize: '30px' }}>{v}</p>
                    <p className="f-jost text-[12px] uppercase tracking-[0.16em] text-slate-400 mt-1">{l}</p>
                  </div>
                ))}
              </div>

              {/* Progress */}
              <div className="bg-white border p-5" style={{ borderColor: '#e2ddd6' }}>
                <p className="f-jost text-[13px] uppercase tracking-[0.16em] text-slate-400 mb-3">Progress</p>
                <div className="space-y-2">
                  {stepLabels.map((label, i) => (
                    <div key={label} className="flex items-center gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"
                        style={{ border: `1px solid ${step > i + 1 ? GOLD : '#e2ddd6'}`, background: step > i + 1 ? GOLD_DIM : 'transparent' }}>
                        {step > i + 1
                          ? <Check size={9} strokeWidth={2.5} style={{ color: GOLD }} />
                          : <span className="f-jost text-[12px]" style={{ color: step === i + 1 ? GOLD : '#94a3b8' }}>{i + 1}</span>}
                      </div>
                      <span className="f-jost text-[13px] uppercase tracking-[0.14em]"
                        style={{ color: step === i + 1 ? NAVY : step > i + 1 ? '#64748b' : '#94a3b8' }}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Pricing Breakdown Modal ── */}
      {showBreakdownModal && (
        <div
          className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center"
          style={{ background: 'rgba(8,14,26,0.75)', backdropFilter: 'blur(8px)' }}
          onClick={() => setShowBreakdownModal(false)}
        >
          <div
            className="relative w-full sm:max-w-lg max-h-[85vh] overflow-y-auto"
            style={{ background: NAVY, border: '1px solid rgba(184,151,90,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ height: '2px', background: `linear-gradient(90deg, ${GOLD}, rgba(184,151,90,0.08))` }} />
            <div className="flex items-center justify-between px-7 pt-6 pb-4"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-3">
                <div className="w-5 h-px" style={{ background: GOLD }} />
                <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                  Trip summary
                </span>
              </div>
              <button
                onClick={() => setShowBreakdownModal(false)}
                className="w-8 h-8 flex items-center justify-center transition-all"
                style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>

            <div className="px-7 py-6">
              <div className="grid grid-cols-3 gap-4 mb-6 pb-5"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                {[
                  [totalNights + '', 'nights'],
                  [groupSize + '',   'guests'],
                  [totalAttractions + '', 'experiences'],
                ].map(([v, l]) => (
                  <div key={l} className="text-center">
                    <p className="f-cormorant text-white font-light leading-none" style={{ fontSize: '32px' }}>{v}</p>
                    <p className="f-jost text-[12px] uppercase tracking-[0.16em] mt-1"
                      style={{ color: 'rgba(255,255,255,0.3)' }}>{l}</p>
                  </div>
                ))}
              </div>

              <PricingBreakdown
                lines={sidebarLines}
                totals={sidebarTotals}
                theme="dark"
                showPerPerson={true}
              />

              {Array.isArray(selectedSerengetiZones) && selectedSerengetiZones.length > 0 && (
                <div className="mt-4 pt-4 space-y-1"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  {selectedSerengetiZones.map(zone => (
                    <div key={zone.key} className="flex items-center gap-2">
                      <MapPin size={9} strokeWidth={1.5} style={{ color: zone.color || GOLD }} />
                      <span className="f-jost text-[12px]" style={{ color: zone.color || GOLD }}>
                        {zone.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {appliedRecommendation && editedSinceBlueprint && step < 6 && (
                <p className="f-jost text-[12px] mt-4 pt-4"
                  style={{ color: 'rgba(245,158,11,0.7)', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  ✦ Edited since blueprint — final price confirmed at Step 6
                </p>
              )}

              {usingBlueprintQuote && !editedSinceBlueprint && (
                <p className="f-jost text-[12px] mt-4 pt-4"
                  style={{ color: 'rgba(255,255,255,0.25)', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  ✦ Blueprint price · server-calculated · confirmed at Step 6
                </p>
              )}

              {!usingBlueprintQuote && summary?.isEstimate && step < 6 && (
                <p className="f-jost text-[12px] mt-4 pt-4"
                  style={{ color: 'rgba(255,255,255,0.25)', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  ✦ Estimate based on lowest available rate · Confirmed in Step 6
                </p>
              )}
            </div>

            <div className="px-7 pb-7">
              <button
                onClick={() => { setShowBreakdownModal(false); setStep(6); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="primary-cta w-full"
                style={{ opacity: step >= 6 ? 0.4 : 1, pointerEvents: step >= 6 ? 'none' : 'auto' }}
              >
                <span>See full server breakdown in Step 6</span>
                <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TourBuilderPage;
